<template>
  <!-- 🔴 An APP SHELL, not a document: the page itself never scrolls. The root fills
       `.v-main`'s content box exactly, the chrome below keeps its natural height, and
       the columns take every remaining pixel and scroll INSIDE themselves. Same shell
       as `TaxPlaygroundV2.vue` — see its style block for why the height is plain CSS
       and not a measured value. -->
  <div class="tpg-page">
    <v-container fluid class="tpg-container">
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar color="primary" class="header-title text-white" data-testid="txl-toolbar">
          <v-btn
            icon="mdi-arrow-left"
            variant="text"
            title="กลับไปหน้ารายการ Tax Log"
            aria-label="กลับไปหน้ารายการ Tax Log"
            data-testid="txl-back"
            @click="goBack"
          />
          <v-toolbar-title class="title">Tax Log — ประวัติแชท{{ isOffline ? ' (จากไฟล์)' : '' }}</v-toolbar-title>
          <v-spacer />
          <!-- 🔴 Same place as the playground's toggle, for the same reason: 64 px of
               chrome that is already being paid for, so offering the collapse costs the
               columns nothing. Inside the card it would have added a row of its own and
               eaten more than it saved. -->
          <v-btn
            variant="text"
            size="small"
            class="text-white mr-2"
            :prepend-icon="headerOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            :title="headerOpen ? 'ยุบแถบสรุป session (คืนพื้นที่ให้คอลัมน์)' : 'แสดงแถบสรุป session'"
            data-testid="txl-header-toggle"
            @click="headerOpen = !headerOpen"
          >{{ headerOpen ? 'ยุบแถบสรุป' : 'แถบสรุป' }}</v-btn>
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <!-- 🔴 The read-only promise, stated before anything else on the page. -->
      <v-alert
        type="info"
        variant="tonal"
        density="compact"
        class="mt-3"
        data-testid="txl-readonly-banner"
      >
        โหมดอ่านอย่างเดียว — ดูประวัติได้เท่านั้น ส่งข้อความหรือแก้ไขไม่ได้
        <template #append>
          <v-chip size="small" variant="tonal" prepend-icon="mdi-eye-outline" data-testid="txl-readonly-chip">
            อ่านอย่างเดียว
          </v-chip>
        </template>
      </v-alert>

      <!-- ── offline mode (route `TaxLogV2Offline`, `meta.offline`) — which file is on
           screen. 🔴 Stated above the header on purpose: every chip below it now
           describes the FILE's copy of the session, not the database, and nothing on
           this page reloads from the server while it is open. -->
      <v-alert
        v-if="isOffline && offlineFile"
        type="info"
        variant="tonal"
        density="compact"
        icon="mdi-file-document-outline"
        class="mt-3"
        data-testid="txl-offline-banner"
      >
        <div data-testid="txl-offline-banner-text">{{ offlineBannerText }}</div>
        <div v-if="offlineWarnings.length" class="mt-1">
          <v-chip
            link
            size="x-small"
            color="warning"
            variant="tonal"
            prepend-icon="mdi-alert-outline"
            :append-icon="offlineWarningsOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            title="คำเตือนที่ server บันทึกไว้ตอน export (เช่น เทิร์นที่ประกอบรายละเอียดไม่สำเร็จ)"
            data-testid="txl-offline-warnings-chip"
            @click="offlineWarningsOpen = !offlineWarningsOpen"
          >คำเตือนในไฟล์ {{ offlineWarnings.length }} รายการ</v-chip>
          <ul v-if="offlineWarningsOpen" class="txl-offline-warnings text-caption mt-1" data-testid="txl-offline-warnings">
            <li v-for="(w, i) in offlineWarnings" :key="'ow-' + i">{{ warningText(w) }}</li>
          </ul>
        </div>
        <template #append>
          <v-btn
            size="small"
            variant="outlined"
            prepend-icon="mdi-folder-open-outline"
            data-testid="txl-offline-open-other"
            @click="openAnotherOfflineFile"
          >เปิดไฟล์อื่น</v-btn>
        </template>
      </v-alert>

      <!-- ── header card (this page's replacement for the playground control bar).
           🔴 Everything in it is a CHIP or a local view selector. There is no CIF
           field, no session field, no version picker and no reset button, because
           none of them could do anything but write. -->
      <v-card v-if="!showOfflinePicker" class="card2 mt-3" data-testid="txl-header-card">
        <v-card-text>
          <v-skeleton-loader v-if="loadingSession" type="list-item-two-line" data-testid="txl-header-skeleton" />

          <!-- Collapsed, the header leaves this one line behind. 🔴 `v-if`, NOT
               `v-show`: Vuetify's `.d-flex` is `display: flex !important`, which the
               inline `display: none` of `v-show` cannot override — the line would stay
               on screen on top of the block it is supposed to replace. -->
          <div
            v-else-if="session && !headerOpen"
            class="d-flex align-center flex-wrap"
            data-testid="txl-header-summary"
          >
            <v-icon size="16" class="mr-1 text-medium-emphasis">mdi-information-outline</v-icon>
            <span class="text-caption text-medium-emphasis">{{ headerSummary }}</span>
            <v-btn
              icon="mdi-chevron-down"
              size="x-small"
              variant="text"
              class="ml-1"
              title="แสดงแถบสรุป session"
              aria-label="แสดงแถบสรุป session"
              data-testid="txl-header-expand"
              @click="headerOpen = true"
            />
          </div>

          <!-- 🔴 Still ONE chain with the skeleton above (`v-else-if`, not a fresh
               `v-if`): a reload keeps `session` from the previous read, so a second
               chain would draw the whole header on top of its own skeleton. -->
          <template v-else-if="session">
            <div class="d-flex align-center flex-wrap ga-2">
              <v-chip
                link
                size="small"
                variant="outlined"
                prepend-icon="mdi-account-outline"
                append-icon="mdi-content-copy"
                title="คัดลอก CIF ที่เข้ารหัส"
                data-testid="txl-chip-user"
                @click="onCopy({ text: session.userID, label: 'CIF' })"
              >
                CIF {{ session.userID || '—' }}
              </v-chip>

              <v-chip
                link
                size="small"
                variant="outlined"
                prepend-icon="mdi-identifier"
                append-icon="mdi-content-copy"
                title="คัดลอก Session ID"
                data-testid="txl-chip-session"
                @click="onCopy({ text: session.sessionID, label: 'Session ID' })"
              >{{ session.sessionID }}</v-chip>

              <v-chip
                size="small"
                :color="sourceColor"
                variant="flat"
                :prepend-icon="session.isPlayground ? 'mdi-flask-outline' : 'mdi-chat-outline'"
                title="แหล่งที่มาของบทสนทนา"
                data-testid="txl-chip-source"
              >{{ sourceTh }}</v-chip>

              <v-chip size="small" variant="outlined" prepend-icon="mdi-cellphone-link" data-testid="txl-chip-channel">
                ช่องทาง {{ session.channel || '—' }}
              </v-chip>

              <v-chip size="small" variant="outlined" prepend-icon="mdi-forum-outline" data-testid="txl-chip-turns">
                {{ (session.turnCount || 0).toLocaleString() }} เทิร์น
              </v-chip>

              <!-- 🔴 นับจาก `forms[]` ที่โหลดมาแล้ว ไม่ใช่จาก `session.*` — DTO ของรายการเลิกส่ง
                   formCount/completedCount แล้ว เพราะหนึ่งแถวในหน้า Tax Log = หนึ่งฟอร์ม -->
              <v-chip size="small" variant="outlined" prepend-icon="mdi-file-document-multiple-outline" data-testid="txl-chip-forms">
                {{ formCount }} ฟอร์ม (จบแล้ว {{ completedFormCount }})
              </v-chip>

              <v-chip size="small" variant="outlined" prepend-icon="mdi-poker-chip" data-testid="txl-chip-tokens">
                {{ (session.totalTokens || 0).toLocaleString() }} token
              </v-chip>

              <v-tooltip location="bottom" max-width="300">
                <template #activator="{ props }">
                  <v-chip
                    v-bind="props"
                    size="small"
                    variant="outlined"
                    prepend-icon="mdi-arrow-collapse-down"
                    data-testid="txl-chip-input-tokens"
                  >{{ formatTokenCount(session.inputTokens) }} tk / {{ formatTokenCost(session.inputCostUsd) }}</v-chip>
                </template>
                {{ tokenRateTooltip(session.inputRateUsdPerThousand, session.inputCostUsd) }}
              </v-tooltip>

              <v-tooltip location="bottom" max-width="300">
                <template #activator="{ props }">
                  <v-chip
                    v-bind="props"
                    size="small"
                    variant="outlined"
                    prepend-icon="mdi-arrow-collapse-up"
                    data-testid="txl-chip-output-tokens"
                  >{{ formatTokenCount(session.outputTokens) }} tk / {{ formatTokenCost(session.outputCostUsd) }}</v-chip>
                </template>
                {{ tokenRateTooltip(session.outputRateUsdPerThousand, session.outputCostUsd) }}
              </v-tooltip>

              <v-spacer />

              <!-- 🔴 Both live-only: an offline page has no server to export from, and a
                   link to `/TaxLogV2/offline` would open an empty picker on anyone else's
                   machine (the file lives in THIS tab's memory only). -->
              <v-btn
                v-if="!isOffline"
                size="small"
                variant="text"
                prepend-icon="mdi-code-json"
                :loading="exportLoading"
                :disabled="exportLoading || !session"
                title="ดาวน์โหลดทั้ง session (ทุกเทิร์น + prompt/คำตอบ LLM เต็ม + ทุกฟอร์ม) เป็นไฟล์ JSON เปิดดูได้ที่หน้า Tax Log → เปิดไฟล์ JSON"
                data-testid="txl-export-json"
                @click="exportJson"
              >ส่งออก JSON</v-btn>
              <v-btn
                v-if="!isOffline"
                size="small"
                variant="text"
                prepend-icon="mdi-link-variant"
                data-testid="txl-copy-session-link"
                @click="copySessionLink"
              >คัดลอกลิงก์หน้านี้</v-btn>
            </div>

            <div class="text-caption text-medium-emphasis mt-1" data-testid="txl-activity">
              เริ่ม {{ fmtDateTime(session.firstActivity) }} · ล่าสุด {{ fmtDateTime(session.lastActivity) }}
            </div>

            <!-- 🔴 A real LHB session restarts the questionnaire, so ONE session can
                 hold many forms (32 in the worst case seen on Local). This selector
                 only decides WHICH form the ฟอร์ม / Tax JSON panels read — it is local
                 view state and calls nothing but `GET /forms/{formId}`. -->
            <v-row align="center" dense class="mt-2">
              <v-col cols="12" md="6" lg="5">
                <v-select
                  :items="formItems"
                  :model-value="selectedFormId"
                  label="ฟอร์มที่ต้องการดู"
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  :loading="loadingForm"
                  :disabled="!formItems.length"
                  :hint="formSelectHint"
                  persistent-hint
                  data-testid="txl-form-select"
                  @update:model-value="onSelectForm"
                >
                  <template #item="{ props: itemProps, item }">
                    <v-list-item v-bind="itemProps" :subtitle="item.raw.subtitle || null" />
                  </template>
                </v-select>
              </v-col>
              <!-- 🔴 EVERY CHIP HERE READS `formState` = the form AS IT STANDS NOW,
                   even while the ฟอร์ม / Tax JSON panels below are showing a
                   playground TURN SNAPSHOT. When those two scopes differ the chip
                   text itself has to say so ("ตอบแล้ว 38/40 ณ ปัจจุบัน" next to a
                   panel labelled "ณ เทิร์นนี้") — a tooltip is not enough, the
                   NUMBER is what gets read. -->
              <v-col cols="12" md="6" lg="7" class="d-flex flex-wrap ga-2 align-center">
                <v-chip
                  v-if="selectedForm"
                  size="x-small"
                  :color="statusColor(selectedForm.status)"
                  variant="tonal"
                  data-testid="txl-chip-formstatus"
                >{{ statusTh(selectedForm.status) }}{{ headerScopeSuffix }}</v-chip>
                <!-- Completed plan re-opened with «แก้ไขแผน»: Status stays Completed, this flag says so. -->
                <v-chip
                  v-if="selectedForm && selectedForm.postCompleteEditStatus"
                  size="x-small"
                  :color="postCompleteEditColor(selectedForm.postCompleteEditStatus)"
                  variant="outlined"
                  data-testid="txl-chip-formpostcompleteedit"
                >{{ postCompleteEditTh(selectedForm.postCompleteEditStatus) }}{{ headerScopeSuffix }}</v-chip>
                <v-chip v-if="selectedForm" size="x-small" variant="outlined" data-testid="txl-chip-formyear">
                  ปีภาษี {{ selectedForm.yearCode || selectedForm.yearID }}
                </v-chip>
                <v-chip v-if="selectedForm" size="x-small" variant="outlined" data-testid="txl-chip-formtaxtext">
                  {{ selectedForm.taxText || 'Tax Plan' }}
                </v-chip>
                <v-chip v-if="progress" size="x-small" variant="outlined" data-testid="txl-chip-progress">
                  ตอบแล้ว {{ progress.answeredCount || 0 }}/{{ progress.totalRequired || 0 }}{{ headerScopeSuffix }}
                </v-chip>
                <v-chip v-if="versions" size="x-small" variant="outlined" data-testid="txl-chip-versions">
                  cfg #{{ versions.configVersionId }} · q #{{ versions.questionVersionId }}{{ headerScopeSuffix }}
                </v-chip>
                <v-chip
                  v-if="turnSnapshotAvailable"
                  size="x-small"
                  color="purple"
                  variant="tonal"
                  prepend-icon="mdi-history"
                  data-testid="txl-chip-scope-note"
                >แผงฟอร์ม / Tax JSON ด้านล่างแสดง ณ เทิร์น #{{ activeTurn.turnNo }}</v-chip>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>

      <!-- ── offline mode with no file yet — the picker REPLACES the page body.
           🔴 The page must carry its own picker: the file is held in memory only
           (`utils/taxLogOfflineStore.js` — tens of MB, too big for web storage), so a
           refresh or a pasted `/TaxLogV2/offline` link lands here with nothing loaded. -->
      <v-card v-if="showOfflinePicker" class="card2 mt-3" data-testid="txl-offline-picker">
        <v-card-title class="d-flex align-center ga-2">
          <v-icon color="primary" size="22">mdi-folder-open-outline</v-icon>
          <span class="text-subtitle-1 font-weight-medium">เปิดไฟล์ Tax Log (JSON)</span>
        </v-card-title>
        <v-card-text>
          <div class="text-body-2 mb-1">
            เลือกไฟล์ที่ได้จากปุ่ม “ส่งออก JSON” ในหน้ารายละเอียดของ Tax Log เพื่อดูประวัติแชททั้ง session
            (ทุกเทิร์น · prompt/คำตอบ LLM · ฟอร์ม) ได้เหมือนหน้าจริง โดยไม่ต้องต่อ server
          </div>
          <div class="text-caption text-medium-emphasis mb-3">
            ไฟล์ถูกอ่านในเบราว์เซอร์นี้เท่านั้น ไม่ถูกส่งขึ้น server — refresh หรือปิดหน้าแล้วต้องเลือกไฟล์ใหม่
          </div>
          <div
            class="txl-drop pa-6 text-center"
            :class="{ 'txl-drop--over': offlineDragOver, 'txl-drop--error': !!offlineError }"
            data-testid="txl-offline-drop"
            @dragover.prevent="offlineDragOver = true"
            @dragleave.prevent="offlineDragOver = false"
            @drop.prevent="onOfflineDrop"
          >
            <v-icon size="40" color="grey">mdi-code-json</v-icon>
            <div class="text-body-2 mt-2">ลากไฟล์ .json มาวางที่นี่ หรือ</div>
            <v-btn
              class="mt-2"
              size="small"
              variant="outlined"
              prepend-icon="mdi-file-upload-outline"
              :loading="offlineReading"
              data-testid="txl-offline-pick"
              @click="pickOfflineFile"
            >เลือกไฟล์ .json</v-btn>
            <input
              ref="offlineFileInput"
              type="file"
              accept="application/json,.json"
              class="d-none"
              data-testid="txl-offline-file"
              @change="onOfflineFileChosen"
            >
          </div>
          <v-alert
            v-if="offlineError"
            type="error"
            variant="tonal"
            density="compact"
            class="mt-3"
            data-testid="txl-offline-error"
          >{{ offlineError }}</v-alert>
        </v-card-text>
      </v-card>

      <!-- ── session 404 / load error ───────────────────────────────────── -->
      <div v-else-if="sessionNotFound" class="text-center pa-8" data-testid="txl-session-notfound">
        <v-icon size="48" color="grey-lighten-1">mdi-link-variant-off</v-icon>
        <div class="mt-2 text-body-2">ไม่พบ session นี้ — อาจถูกลบ หรือเป็นของบริษัทอื่น</div>
        <v-btn class="mt-3" size="small" variant="outlined" data-testid="txl-notfound-back" @click="goBack">
          กลับไปหน้ารายการ
        </v-btn>
      </div>

      <v-alert
        v-else-if="sessionError"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-3"
        data-testid="txl-session-error"
      >
        {{ sessionError }}
        <template #append>
          <!-- Offline: retrying reads the same file again — say what is true instead. -->
          <span v-if="isOffline" class="text-caption" data-testid="txl-session-offline-note">ข้อมูลในไฟล์ไม่ครบ</span>
          <v-btn v-else size="small" variant="outlined" prepend-icon="mdi-refresh" data-testid="txl-session-retry" @click="loadAll">
            ลองใหม่
          </v-btn>
        </template>
      </v-alert>

      <template v-else>
        <!-- 🔴 PAGE LEVEL ON PURPOSE. This alert used to live inside the desktop
             timeline column, so at xs/sm — and in collapsed / เต็มความกว้าง mode —
             a failed `GET /sessions/{id}/turns` left `turns = []` and the only
             text on screen was the chat panel's "ไม่มีบทสนทนาใน session นี้": a
             transport failure rendered as a FACT about the session, with no way
             to retry. It has to render on every breakpoint. -->
        <v-alert
          v-if="turnsError"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-3"
          data-testid="txl-turns-error"
        >
          {{ turnsError }}
          <template #append>
            <span v-if="isOffline" class="text-caption" data-testid="txl-turns-offline-note">ข้อมูลในไฟล์ไม่ครบ</span>
            <v-btn v-else size="x-small" variant="outlined" prepend-icon="mdi-refresh" data-testid="txl-turns-retry" @click="loadTurns()">
              ลองใหม่
            </v-btn>
          </template>
        </v-alert>

        <!-- Tablet + mobile: the timeline becomes a horizontal strip. -->
        <div v-if="!mdAndUp && turns.length" class="tpg-strip mt-3" data-testid="txl-turn-strip">
          <div
            v-for="row in timelineDesc"
            :key="row.turnKey"
            class="tpg-strip-card"
            :class="{ 'tpg-turn--active': isActiveTurn(row.turnKey) }"
            :data-testid="'txl-turn-card-' + row.turnNo"
            @click="onOpenTurn(row.turnKey)"
          >
            <div class="text-caption font-weight-medium">#{{ row.turnNo }} · {{ fmtTime(row.createdDate) }}</div>
            <div class="text-caption text-truncate">{{ turnPreview(row) }}</div>
            <v-chip v-if="isErrorTurn(row)" size="x-small" color="error" variant="tonal">ผิดพลาด</v-chip>
          </div>

          <!-- Same paging affordance the desktop timeline has — the strip is the
               ONLY way to reach an older turn below md. -->
          <div v-if="hasMoreTurns && !isOffline" class="tpg-strip-card d-flex align-center justify-center" data-testid="txl-turns-more-strip">
            <v-btn size="x-small" variant="text" :loading="loadingTurns" @click="loadMoreTurns">
              โหลดเทิร์นเพิ่ม
            </v-btn>
          </div>
        </div>

        <v-row no-gutters class="mt-3 tpg-row">
          <!-- ── A · turn timeline (desktop only, collapsible to a rail) ── -->
          <v-col
            v-if="mdAndUp && !inspectorFull"
            :cols="colSpans.timeline.cols"
            :md="colSpans.timeline.md"
            :lg="colSpans.timeline.lg"
            class="tpg-col pr-2"
            :class="{ 'tpg-col--rail': timelineCollapsed }"
            role="region"
            aria-label="ไทม์ไลน์เทิร์น"
          >
            <div v-if="timelineCollapsed" class="tpg-rail-inner">
              <v-btn
                icon="mdi-chevron-right"
                size="x-small"
                variant="text"
                title="แสดงคอลัมน์ไทม์ไลน์"
                aria-label="แสดงคอลัมน์ไทม์ไลน์"
                data-testid="txl-timeline-expand"
                @click="timelineCollapsed = false"
              />
              <div class="tpg-rail-label">เทิร์น ({{ turnsTotal }})</div>
            </div>

            <template v-else>
              <div class="d-flex align-center mb-2">
                <span class="text-subtitle-2">เทิร์น ({{ turnsTotal }})</span>
                <v-spacer />
                <v-btn
                  v-if="!isOffline"
                  icon="mdi-refresh"
                  size="x-small"
                  variant="text"
                  title="โหลดไทม์ไลน์ใหม่"
                  aria-label="โหลดไทม์ไลน์ใหม่"
                  :loading="loadingTurns"
                  data-testid="txl-timeline-refresh"
                  @click="loadTurns()"
                />
                <v-btn
                  icon="mdi-chevron-left"
                  size="x-small"
                  variant="text"
                  title="ยุบคอลัมน์ไทม์ไลน์ (คืนพื้นที่ให้ฝั่งขวา)"
                  aria-label="ยุบคอลัมน์ไทม์ไลน์"
                  data-testid="txl-timeline-collapse"
                  @click="timelineCollapsed = true"
                />
              </div>

              <!-- 🔴 `turnsError` is NOT rendered here any more — it moved to page
                   level (just under `<template v-else>`) so that a failed turns
                   fetch is visible at xs/sm and in collapsed / เต็มความกว้าง mode
                   too. Do not re-add a copy: two alerts, one retry each. -->
              <v-skeleton-loader v-if="loadingTurns && !turns.length" type="paragraph" data-testid="txl-timeline-skeleton" />

              <div
                v-else-if="!turns.length"
                class="text-medium-emphasis text-caption pa-2 text-center"
                data-testid="txl-timeline-empty"
              >
                <v-icon size="28" color="grey-lighten-1">mdi-history</v-icon>
                <div class="mt-1">ไม่มีเทิร์นใน session นี้</div>
              </div>

              <template v-else>
                <v-card
                  v-for="row in timelineDesc"
                  :key="row.turnKey"
                  class="mb-2 tpg-turn-card"
                  :class="{ 'tpg-turn--active': isActiveTurn(row.turnKey) }"
                  density="compact"
                  variant="outlined"
                  :data-testid="'txl-turn-card-' + row.turnNo"
                  @click="onOpenTurn(row.turnKey)"
                >
                  <v-card-text class="py-2 px-3">
                    <div class="d-flex align-center">
                      <span class="text-caption font-weight-medium">#{{ row.turnNo }}</span>
                      <span class="text-caption text-medium-emphasis ml-2">{{ fmtTime(row.createdDate) }}</span>
                      <v-spacer />
                      <span class="text-caption text-medium-emphasis">{{ row.llmCallCount || 0 }} call</span>
                    </div>
                    <div class="text-caption text-truncate">{{ turnPreview(row) }}</div>
                    <div class="d-flex flex-wrap ga-1 mt-1">
                      <v-chip size="x-small" variant="outlined" :title="'token ทั้งหมดของเทิร์นนี้'">
                        {{ (row.totalTokens || 0).toLocaleString() }} tk
                      </v-chip>
                      <v-chip
                        v-if="isErrorTurn(row)"
                        size="x-small"
                        color="error"
                        variant="tonal"
                        prepend-icon="mdi-alert-circle-outline"
                        :title="row.errorMessage || 'เทิร์นนี้จบด้วยข้อผิดพลาด'"
                        :data-testid="'txl-turn-error-' + row.turnNo"
                      >ผิดพลาด</v-chip>
                    </div>
                  </v-card-text>
                </v-card>

                <div v-if="hasMoreTurns && !isOffline" class="text-center">
                  <v-btn
                    size="small"
                    variant="text"
                    :loading="loadingTurns"
                    data-testid="txl-turns-more"
                    @click="loadMoreTurns"
                  >โหลดเทิร์นเพิ่ม ({{ turns.length }}/{{ turnsTotal }})</v-btn>
                </div>
              </template>
            </template>
          </v-col>

          <!-- ── B · chat (its own column from sm up; a tab at xs) ───────── -->
          <v-col
            v-if="!isXs && !inspectorFull"
            :cols="colSpans.chat.cols"
            :sm="colSpans.chat.sm"
            :md="colSpans.chat.md"
            :lg="colSpans.chat.lg"
            class="tpg-col px-2"
            role="region"
            aria-label="บทสนทนา (อ่านอย่างเดียว)"
          >
            <TaxLogChatPanelV2
              :bubbles="bubbles"
              :active-turn-key="activeTurnKey"
              :selected-form-id="selectedFormId"
              :loading="loadingTurns && !turns.length"
              :turn-count="turnsTotal"
              @select-turn="onOpenTurn"
              @select-form="onSelectForm"
            />
          </v-col>

          <!-- ── C · inspector ──────────────────────────────────────────── -->
          <v-col
            :cols="colSpans.inspector.cols"
            :sm="colSpans.inspector.sm"
            :md="colSpans.inspector.md"
            :lg="colSpans.inspector.lg"
            class="tpg-col tpg-inspector"
            :class="{ 'pl-2': !isXs && !inspectorFull }"
            role="region"
            aria-label="ตัวตรวจสอบเทิร์น"
          >
            <div class="tpg-inspector-head d-flex align-center flex-wrap ga-2">
              <span class="text-caption">
                <template v-if="activeTurn">
                  กำลังดูเทิร์น #{{ activeTurn.turnNo }} · {{ fmtTime(activeTurn.createdDate) }}
                </template>
                <template v-else>ยังไม่ได้เลือกเทิร์น</template>
              </span>
              <v-chip
                v-if="activeTurn && activeTurn.modelName"
                size="x-small"
                variant="tonal"
                :title="'โมเดลที่ใช้ในเทิร์นนี้'"
                data-testid="txl-active-model"
              >{{ activeTurn.modelName }}</v-chip>
              <v-progress-circular v-if="loadingTurn" size="16" width="2" indeterminate data-testid="txl-turn-loading" />
              <v-spacer />
              <v-btn
                v-if="activeTurn && !isOffline"
                size="x-small"
                variant="text"
                prepend-icon="mdi-link-variant"
                data-testid="txl-copy-turn-link"
                @click="copyTurnLink"
              >คัดลอกลิงก์เทิร์นนี้</v-btn>
              <v-btn
                v-if="mdAndUp"
                size="x-small"
                variant="text"
                :prepend-icon="inspectorFull ? 'mdi-arrow-collapse-horizontal' : 'mdi-arrow-expand-horizontal'"
                :title="inspectorFull ? 'กลับไปแบบหลายคอลัมน์' : 'ขยายแผงนี้เต็มความกว้าง (ซ่อนไทม์ไลน์+แชท)'"
                data-testid="txl-inspector-full"
                @click="inspectorFull = !inspectorFull"
              >{{ inspectorFull ? 'ย่อกลับ' : 'เต็มความกว้าง' }}</v-btn>
            </div>

            <v-alert
              v-if="turnError"
              type="error"
              variant="tonal"
              density="compact"
              class="mb-2"
              data-testid="txl-turn-error"
            >
              {{ turnError }}
              <template #append>
                <span v-if="isOffline" class="text-caption" data-testid="txl-turn-offline-note">ไม่มีในไฟล์</span>
                <v-btn v-else size="x-small" variant="text" data-testid="txl-turn-retry" @click="reloadActiveTurn">ลองใหม่</v-btn>
              </template>
            </v-alert>

            <div class="tpg-pane">
              <v-tabs
                :model-value="activeTab"
                density="compact"
                show-arrows
                class="tpg-tabs"
                @update:model-value="inspectorTab = $event"
              >
                <v-tab
                  v-for="t in tabDefs"
                  :key="t.value"
                  :value="t.value"
                  :color="t.color"
                  :data-testid="'txl-tab-' + t.value"
                >
                  <v-icon v-if="t.icon" size="14" class="mr-1">{{ t.icon }}</v-icon>
                  {{ t.label }}
                </v-tab>
              </v-tabs>

              <v-window :model-value="activeTab" class="mt-2">
                <!-- xs only — the chat has no column of its own at that breakpoint. -->
                <v-window-item v-if="isXs" value="chat">
                  <TaxLogChatPanelV2
                    :bubbles="bubbles"
                    :active-turn-key="activeTurnKey"
                    :selected-form-id="selectedFormId"
                    :loading="loadingTurns && !turns.length"
                    :turn-count="turnsTotal"
                    @select-turn="onOpenTurn"
                    @select-form="onSelectForm"
                  />
                </v-window-item>

                <v-window-item value="form">
                  <div class="tpg-form-pane">
                    <!-- 🔴 Production keeps NO per-turn snapshot (only the playground
                         writes `tax.TaxPlaygroundTurnV2`), so the form shown is the
                         form as it stands NOW. Say so, every time, or the panel reads
                         as "the form at เทิร์น #N" and quietly lies. -->
                    <v-alert
                      :type="configFormIsSnapshot ? 'success' : 'info'"
                      variant="tonal"
                      density="compact"
                      class="mb-2"
                      data-testid="txl-form-scope"
                    >{{ configFormScopeTh }}</v-alert>

                    <v-alert
                      v-if="formError"
                      type="error"
                      variant="tonal"
                      density="compact"
                      class="mb-2"
                      data-testid="txl-form-error"
                    >
                      {{ formError }}
                      <template #append>
                        <span v-if="isOffline" class="text-caption" data-testid="txl-form-offline-note">ไม่มีในไฟล์</span>
                        <v-btn v-else size="x-small" variant="text" data-testid="txl-form-retry" @click="loadFormState">ลองใหม่</v-btn>
                      </template>
                    </v-alert>

                    <v-skeleton-loader v-if="loadingForm && !configFormIsSnapshot" type="article" data-testid="txl-form-skeleton" />
                    <!-- 🔴 `scope-label` / `scope-hint` are why this panel stopped
                         contradicting the alert above it: its own defaults are the
                         playground's "…(ณ turn นี้)" strings, which are a LIE on a
                         production session (no per-turn snapshot exists). The
                         mounting page owns the claim about WHICH point in time
                         these values describe. -->
                    <TaxPlaygroundConfigFormPanelV2
                      v-else-if="configForm"
                      :config-form="configForm"
                      :changed-paths="changedPaths"
                      :scope-label="configFormPanelScopeLabel"
                      :scope-hint="configFormPanelScopeHint"
                      @copy="onCopy"
                    />
                    <div v-else class="text-medium-emphasis text-caption pa-2" data-testid="txl-form-empty">
                      ไม่มีข้อมูลฟอร์มของ session นี้
                    </div>
                  </div>
                </v-window-item>

                <v-window-item value="llm">
                  <div v-if="!activeTurn" class="text-medium-emphasis text-caption pa-2" data-testid="txl-llm-noturn">
                    เลือกเทิร์นจากไทม์ไลน์หรือคลิกที่ข้อความในแชท เพื่อดู prompt ที่ส่งเข้า LLM
                  </div>
                  <template v-else>
                    <v-skeleton-loader v-if="loadingTurn" type="article" data-testid="txl-llm-skeleton" />
                    <template v-else>
                      <!-- Production prompts come from `log.Log_Monitor.ContentRequest`
                           and carry NO promptLayers — the viewer degrades to the
                           system-prompt / userText / rawResponse blocks on its own. -->
                      <div class="text-caption text-medium-emphasis pb-1" data-testid="txl-llm-source">
                        {{ llmSourceTh }}
                      </div>
                      <!-- `allow-edit` off offline: the file's editRefs point at config rows of
                           the environment it was exported FROM, which this tab may not be. -->
                      <TaxPlaygroundPromptViewerV2
                        :llm-calls="llmCalls"
                        :askable-count="askableCount"
                        :allow-edit="!isOffline"
                        @open-edit="openEdit"
                        @copy="onCopy"
                      />
                    </template>
                  </template>
                </v-window-item>

                <v-window-item value="json">
                  <v-alert
                    :type="taxStateIsSnapshot ? 'success' : 'info'"
                    variant="tonal"
                    density="compact"
                    class="mb-2"
                    data-testid="txl-json-scope"
                  >{{ taxStateScopeTh }}</v-alert>

                  <!-- 🔴 This pane MUST mirror the ฟอร์ม tab's loading / error
                       states. It used to have neither, so switching forms left the
                       PREVIOUS form's JSON on screen under the new form's scope
                       label, and a 4xx/5xx printed
                       "ยังไม่มีข้อมูลฟอร์มของ session นี้" — a transport failure
                       stated as a fact about the form, with no retry on this tab. -->
                  <v-alert
                    v-if="formError && !taxStateIsSnapshot"
                    type="error"
                    variant="tonal"
                    density="compact"
                    class="mb-2"
                    data-testid="txl-json-error"
                  >
                    {{ formError }}
                    <template #append>
                      <span v-if="isOffline" class="text-caption" data-testid="txl-json-offline-note">ไม่มีในไฟล์</span>
                      <v-btn v-else size="x-small" variant="text" data-testid="txl-json-retry" @click="loadFormState">ลองใหม่</v-btn>
                    </template>
                  </v-alert>

                  <v-skeleton-loader
                    v-if="loadingForm && !taxStateIsSnapshot"
                    type="article"
                    data-testid="txl-json-skeleton"
                  />

                  <template v-else>
                    <v-alert
                      v-if="taxJsonTooBig"
                      type="info"
                      density="compact"
                      variant="tonal"
                      class="mb-2"
                      data-testid="txl-json-large"
                    >
                      JSON ก้อนนี้ใหญ่ ({{ taxJsonKb }} KB) — แสดงแบบย่อเพื่อไม่ให้หน้าค้าง
                      <template #append>
                        <v-btn size="x-small" variant="text" data-testid="txl-json-showall" @click="taxJsonShowAll = true">
                          แสดงทั้งหมด
                        </v-btn>
                      </template>
                    </v-alert>

                    <div class="tpg-json-pane">
                      <div class="d-flex justify-end">
                        <v-btn
                          icon="mdi-content-copy"
                          size="x-small"
                          variant="text"
                          title="คัดลอก Tax JSON"
                          aria-label="คัดลอก Tax JSON"
                          data-testid="txl-json-copy"
                          @click="onCopy({ text: taxStateText, label: 'Tax JSON' })"
                        />
                      </div>
                      <pre class="code-block" data-testid="txl-tax-json">{{ taxStateText }}</pre>
                    </div>
                  </template>
                </v-window-item>

                <v-window-item value="trace">
                  <div v-if="!activeTurn" class="text-medium-emphasis text-caption pa-2" data-testid="txl-trace-noturn">
                    เลือกเทิร์นเพื่อดู trace และขั้นตอนการทำงานของ flow
                  </div>
                  <template v-else>
                    <v-skeleton-loader v-if="loadingTurn" type="article" data-testid="txl-trace-skeleton" />
                    <template v-else>
                      <!-- Flow steps (production turns only — `ChatWorkFlow` of Log_Message). -->
                      <div v-if="steps.length" class="mb-3" data-testid="txl-steps">
                        <div class="text-caption font-weight-medium mb-1">ขั้นตอนใน flow ({{ steps.length }} step)</div>
                        <div
                          v-for="(st, i) in steps"
                          :key="'st-' + i"
                          class="txl-step"
                          :data-testid="'txl-step-' + i"
                        >
                          <div class="d-flex align-center flex-wrap ga-1">
                            <span class="text-caption font-weight-medium">{{ i + 1 }}. {{ st.node || '—' }}</span>
                            <v-chip size="x-small" :color="stepStatusColor(st.status)" variant="tonal">
                              {{ st.status || '—' }}
                            </v-chip>
                            <span class="text-caption text-medium-emphasis">{{ stepMs(st) }}</span>
                          </div>
                          <div v-if="stepPreview(st)" class="text-caption text-medium-emphasis text-truncate">
                            {{ stepPreview(st) }}
                          </div>
                          <div v-if="st.errorMessage" class="text-caption text-error">{{ st.errorMessage }}</div>
                        </div>
                      </div>

                      <div class="d-flex align-center">
                        <span class="text-caption font-weight-medium">Trace</span>
                        <v-spacer />
                        <v-btn
                          icon="mdi-content-copy"
                          size="x-small"
                          variant="text"
                          title="คัดลอก trace"
                          aria-label="คัดลอก trace"
                          data-testid="txl-trace-copy"
                          @click="onCopy({ text: traceText, label: 'Trace' })"
                        />
                      </div>
                      <pre class="code-block" data-testid="txl-trace">{{ traceText }}</pre>
                    </template>
                  </template>
                </v-window-item>

                <v-window-item value="flow">
                  <!-- 🔴 DELIBERATELY NO `v-if="!activeTurn"` / `v-skeleton-loader` wrapper
                       here, unlike the Prompt & LLM and Trace tabs above. Those own their
                       empty states because the thing they render (a viewer, a <pre>) has no
                       opinion about missing data. This panel does: `flow == null` means one
                       of THREE different things (the turn recorded no step · the turn
                       predates the feature · the playground row hit its 1 MB cap and `flow`
                       was shed) plus a fourth wire shape (`truncated: true` with an empty
                       `steps[]`), and it says which — but only if it is allowed to render.
                       A wrapper here would shadow all four with one generic line, so the
                       three facts it needs travel as props instead: `loading`,
                       `turn-selected` (≠ `flow != null`) and `source` + `trace-text`.

                       `trace-text` is the SAME computed the Trace tab prints, fallback
                       strings and all, on purpose: the panel only pattern-matches the one
                       "flow ถูกตัด…" guard line in it, which neither Thai fallback can
                       produce, and feeding it anything else would let this tab claim a
                       truncation the admin cannot find in the tab next door.

                       The panel scrolls inside itself (`max-height: 52vh`), so it gets no
                       scrolling wrapper either — `.tpg-pane` is the only scroller here. -->
                  <TaxTurnFlowPanelV2
                    :flow="turnFlow"
                    :loading="loadingTurn"
                    :turn-selected="turnSelected"
                    :source="turnSource"
                    :trace-text="traceText"
                    @copy="onCopy"
                  />
                </v-window-item>
              </v-window>
            </div>
          </v-col>
        </v-row>
      </template>

      <v-snackbar v-model="snackbar" :color="snackbarColor" location="top" :timeout="snackbarTimeout">
        {{ snackbarMessage }}
        <template #actions>
          <v-btn variant="text" @click="snackbar = false">Close</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
// Tax Log V2 — READ-ONLY viewer of ONE Tax Planning V2 chat session.
//
// The sibling of `TaxPlaygroundV2.vue`: same three-column desktop layout, same
// responsive branches (xs tabs · sm strip · md+ 3 columns), same `<style scoped>`,
// same four inspector tabs (ฟอร์ม / Prompt & LLM / Tax JSON / Trace). What it does
// NOT have is everything that writes.
//
// ─────────────────────────────────────────────────────────────────────────────
// 🔴 FIVE THINGS THAT LOOK LIKE STYLE BUT ARE LOAD-BEARING:
//
// 1. 🔴 READ-ONLY BY CONSTRUCTION. `services/taxLogV2Api.js` is the only data
//    import and it defines no post/put/patch/delete at all; `taxPlaygroundV2Api`
//    and `utils/taxPlaygroundV2Store.js` are NOT imported — not here and not
//    transitively: the one constant the shared `TaxPlaygroundConfigFormPanelV2`
//    needs (`COUNTS_ORDER`) lives in the dependency-free
//    `utils/taxPlaygroundV2Counts.js`, precisely so that mounting that panel does
//    not drag the playground store (and its `reactive` singleton + POST helpers)
//    into this page. `TaxPlaygroundChatPanelV2.vue` is not imported either (it
//    carries a composer). Do not add any of them back — every one of them
//    re-opens a write path. If a future edit makes the panel import the store
//    again, this page still has NO reachable write call, but the guarantee stops
//    being checkable by reading the import graph, which is the whole point.
//    🔴 `utils/taxCardMarker.js` (below) does NOT dent that guarantee and is not a
//    counter-example: it is a dependency-free PURE PARSER (zero imports of its own,
//    unit-tested under plain Node), the same reason `utils/taxPlaygroundV2Counts.js`
//    is allowed here. Same for `components/TaxSummaryCardV2.vue`, which the chat panel
//    mounts: its only import is the equally dependency-free `utils/taxCardFormat.js`.
//    ไล่ import graph จริงแล้วทั้งสองสายจบที่ leaf ไม่มีสายไหนแตะ store/api ของ playground —
//    ถ้าจะเพิ่มไฟล์ใหม่เข้ามาอีก ต้องไล่ซ้ำแบบเดียวกันก่อน
//    🔴 โหมด offline (route `TaxLogV2Offline`, `meta.offline`) เพิ่ม import ที่ **อนุญาต** อีก 2 ตัว:
//    · `utils/taxLogOfflineFile.js` — ไม่มี import เลย (เทสต์ด้วย Node เปล่า) · `createOfflineTaxLogApi`
//      คือ **แหล่งข้อมูลที่สอง แบบอ่านอย่างเดียว** ที่มี contract เดียวกับ `taxLogV2Api` ทุก method
//      ที่หน้านี้เรียก (Promise · 404 ⇒ `err.httpStatus === 404`) ตอบจากไฟล์ export ไม่ยิง network
//      และไม่ mutate ไฟล์ — หน้านี้เลือกแหล่งผ่าน computed `api` ตัวเดียว (ดู `api()` ด้านล่าง)
//    · `utils/taxLogOfflineStore.js` — import แค่ `reactive` จาก vue; ถือไฟล์ที่เปิดอยู่ (ไม่มี write ใด ๆ)
//    ทั้งสองไม่แตะ store/api ของ playground ⇒ กฎข้อนี้ยังตรวจได้ด้วยการอ่าน import graph เหมือนเดิม
//    `taxLogV2Api` ถูกเรียกตรง ๆ ที่เดียวคือ `exportSession` ของปุ่ม "ส่งออก JSON" (ยังเป็น GET)
//
// 2. 🔴 A SESSION HAS MANY FORMS. A real LHB session restarts the questionnaire,
//    so one `SessionID` accumulates many `tax.TaxFormV2` rows (32 in the worst
//    case measured on Local). The page is therefore keyed by SESSION for the
//    header/transcript and by FORM for the ฟอร์ม / Tax JSON panels — hence the
//    form selector, which is pure view state (`GET /forms/{formId}`, nothing more).
//    🔴 The Tax Log LIST is now one row per (form × session) pair, so it deep-links
//    the form it means via `?form=<id>` and this page must honour it over the
//    newest-first default (`loadSession` → `deepLinkFormId`). `?form=` and `?turn=`
//    are INDEPENDENT keys: every `$router.replace` here merges the query rather
//    than replacing it, or one silently erases the other.
//
// 3. 🔴 PRODUCTION HAS NO PER-TURN SNAPSHOT. Only the playground writes
//    `tax.TaxPlaygroundTurnV2`; `TaxPlanningV2NodeProcessor` fills that debug sink
//    for playground turns only. So for a production turn the ฟอร์ม and Tax JSON
//    panels show the form AS IT STANDS NOW, and both say so in Thai. Only when
//    `turn.source === 'playground'` and `turn.hasSnapshot` do they switch to the
//    turn's own `configForm`/`taxState` and relabel themselves "ณ เทิร์นนี้".
//
// 4. 🔴 `turnKey` ("lm:160195" / "pg:812") IS A KEY, NOT A ROUTE PARAM. It keys
//    `v-for`s and selection only. Fetching branches on `turn.source` and passes
//    the numeric `logMessageID` / `playgroundTurnID`. The deep link uses `turnNo`
//    (1-based and page-independent), never `turnKey`.
//
// 5. 🔴 `TaxPlaygroundPromptViewerV2` is REUSED, changed only ADDITIVELY (the
//    playground passes neither addition and renders exactly as before):
//    `allow-edit` (default true — this page turns it off offline) and a
//    "Request body ที่ส่งจริง" block that renders only when a call carries
//    `rawRequest` (export files only). Production turns now
//    arrive WITH `promptLayers` — the backend splits the stored prompt back into
//    its sections (`TaxPromptStackV2.SplitStack`) — but those layers carry TEXT
//    ONLY: no `source`, no `editRef`, because production persists no provenance.
//    The viewer already keys colour on `source` with a neutral fallback and hides
//    the edit buttons behind `hasEditLink`, so the layered view works here and the
//    only thing it must not do is claim WHERE a layer came from.
// ─────────────────────────────────────────────────────────────────────────────
import { taxLogV2Api } from '../services/taxLogV2Api';
import {
  createOfflineTaxLogApi,
  parseTaxLogExport,
  buildTaxLogExportFileName,
  describeTaxLogExport,
} from '../utils/taxLogOfflineFile';
import { taxLogOfflineStore, setOfflineTaxLog, clearOfflineTaxLog } from '../utils/taxLogOfflineStore';
import { splitTaxCards, parseTaxCardsField } from '../utils/taxCardMarker';
import { resolveTurnFormIds } from '../utils/taxLogChatSegments.js';
import { fmtThaiDateTime, fmtThaiDayTime, fmtThaiRelative, fmtThaiTime } from '../utils/thaiTime.js';
import TaxLogChatPanelV2 from '../components/TaxLogChatPanelV2.vue';
import TaxPlaygroundConfigFormPanelV2 from '../components/TaxPlaygroundConfigFormPanelV2.vue';
import TaxPlaygroundPromptViewerV2 from '../components/TaxPlaygroundPromptViewerV2.vue';
// 🔴 Safe under rule 1 above: `TaxTurnFlowPanelV2.vue` has ZERO imports of its own
// (checked, not assumed), so mounting it drags nothing — no store, no playground API,
// no composer — into this read-only page. Re-check that if it ever grows an import.
import TaxTurnFlowPanelV2 from '../components/TaxTurnFlowPanelV2.vue';

/** Above this size a JSON blob is shown compact — pretty-printing it freezes the tab. */
const JSON_BIG_BYTES = 200 * 1024;

/** One request covers almost every real session; the rest is paged in on demand. */
const TURN_PAGE_SIZE = 200;

/**
 * Offline adapter per opened file — `file` (the store's proxy, stable per file) → `{ id, api }`.
 * 🔴 Memoised OUTSIDE the component on purpose: `api` / `dataSourceKey` re-evaluate whenever
 * `$route` changes (every `?turn=` write does), and a fresh adapter each time would look like a
 * new data source and reload the whole page. `id` is what tells two files apart even when both
 * are exports of the SAME session (same `sessionID`, different `exportedAt`).
 */
const offlineApiCache = new WeakMap();
let offlineApiSeq = 0;
function offlineApiFor(file) {
  let hit = offlineApiCache.get(file);
  if (!hit) {
    offlineApiSeq += 1;
    hit = { id: offlineApiSeq, api: createOfflineTaxLogApi(file) };
    offlineApiCache.set(file, hit);
  }
  return hit;
}

const FORM_STATUS_TH = {
  InProgress: 'กำลังตอบ',
  Completed: 'ตอบครบแล้ว',
  Abandoned: 'ถูกทิ้ง',
};

const FORM_STATUS_COLOR = {
  InProgress: 'primary',
  Completed: 'success',
  Abandoned: 'grey',
};

// TaxFormV2.PostCompleteEditStatus (null / Editing / Edited) — a second chip beside the status chip.
const POST_COMPLETE_EDIT_TH = {
  Editing: 'กำลังแก้หลังจบ',
  Edited: 'เคยแก้หลังจบ',
};

const POST_COMPLETE_EDIT_COLOR = {
  Editing: 'info',
  Edited: 'grey',
};

export default {
  name: 'TaxLogV2Detail',
  components: {
    TaxLogChatPanelV2,
    TaxPlaygroundConfigFormPanelV2,
    TaxPlaygroundPromptViewerV2,
    TaxTurnFlowPanelV2,
  },
  data() {
    return {
      flagNav: true,
      today: new Date().toLocaleDateString(),

      // ── server data (all of it read with GET) ────────────────────────
      session: null,
      forms: [],
      selectedFormId: 0,
      formState: null,
      turns: [],
      turnsTotal: 0,
      turnsPage: 1,
      activeTurnKey: null,
      turnDetail: null,

      // ── flags ────────────────────────────────────────────────────────
      loadingSession: false,
      loadingTurns: false,
      loadingTurn: false,
      loadingForm: false,
      sessionNotFound: false,
      sessionError: null,
      turnsError: null,
      turnError: null,
      formError: null,
      exportLoading: false,

      // ── offline mode (file picker / banner) ──────────────────────────
      offlineDragOver: false,
      offlineReading: false,
      offlineError: null,
      offlineWarningsOpen: false,

      // ── display-only state ───────────────────────────────────────────
      inspectorTab: null,
      headerOpen: false,
      timelineCollapsed: false,
      inspectorFull: false,
      taxJsonShowAll: false,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      snackbarTimeout: 3500,
    };
  },
  computed: {
    // ── data source (live API ↔ offline file) ──────────────────────────
    /** Route `TaxLogV2Offline` (`/TaxLogV2/offline`) — same component, reads a file instead of the API. */
    isOffline() {
      return Boolean(this.$route && this.$route.meta && this.$route.meta.offline === true);
    },
    /** The export envelope picked on this page (`parseTaxLogExport(...).file`), or null. */
    offlineFile() {
      return taxLogOfflineStore.file;
    },
    offlineFileName() {
      return taxLogOfflineStore.fileName;
    },
    /** Offline with nothing loaded yet — the picker replaces the page body. */
    showOfflinePicker() {
      return this.isOffline && !this.offlineFile;
    },
    /**
     * 🔴 THE seam: every read on this page goes through `this.api`, never `taxLogV2Api`
     * directly (the lone exception is `exportJson`, which is live-only). Offline it is the
     * read-only file adapter (same method names, same shapes, same 404 contract); with no
     * file yet it is `null` and every loader returns early — see `loadAll`.
     */
    api() {
      if (!this.isOffline) return taxLogV2Api;
      return this.offlineFile ? offlineApiFor(this.offlineFile).api : null;
    },
    /**
     * One string per data source — the ONE thing the reload watcher looks at.
     * 🔴 A string, not an object: it is recomputed on every `$route` change (each
     * `?turn=` / `?form=` write), and only a value-equal result keeps that from
     * reloading the page. The file id is in it so that opening a second export of
     * the SAME session (same `sessionId`) still reloads.
     */
    dataSourceKey() {
      if (!this.isOffline) return `live|${this.sessionId}`;
      if (!this.offlineFile) return 'offline|';
      return `offline|${offlineApiFor(this.offlineFile).id}|${this.sessionId}`;
    },
    /** What the file says about itself (`describeTaxLogExport`) — banner only. */
    offlineInfo() {
      return this.offlineFile ? describeTaxLogExport(this.offlineFile) : null;
    },
    offlineWarnings() {
      return (this.offlineInfo && this.offlineInfo.warnings) || [];
    },
    /** "กำลังดูจากไฟล์ … · export เมื่อ … โดย … · จาก … · N เทิร์น / M ฟอร์ม" — parts the file lacks are dropped. */
    offlineBannerText() {
      const info = this.offlineInfo;
      if (!info) return '';
      const parts = [`กำลังดูจากไฟล์ ${this.offlineFileName || '(ไม่มีชื่อไฟล์)'}`];
      // เวลาไทยด้วย helper ตัวเดียวกับ "เริ่ม / ล่าสุด" ของ header (ค่าในไฟล์เป็น UTC ไม่มี Z แบบเดียวกับ wire)
      parts.push(`export เมื่อ ${this.fmtDateTime(info.exportedAt)}${info.exportedBy ? ` โดย ${info.exportedBy}` : ''}`);
      if (info.environment) parts.push(`จาก ${info.environment}`);
      parts.push(`${info.turnCount.toLocaleString()} เทิร์น / ${info.formCount.toLocaleString()} ฟอร์ม`);
      return parts.join(' · ');
    },

    // ── route / breakpoints ────────────────────────────────────────────
    /** Offline: the file's own session — the offline route has no `:sessionId` param. */
    sessionId() {
      if (this.isOffline) {
        const s = this.offlineFile && this.offlineFile.session;
        return String((s && s.sessionID) || '');
      }
      return String((this.$route.params && this.$route.params.sessionId) || '');
    },
    display() {
      return (this.$vuetify && this.$vuetify.display) || {};
    },
    mdAndUp() {
      return Boolean(this.display.mdAndUp);
    },
    isXs() {
      return Boolean(this.display.xs);
    },

    // ── header ─────────────────────────────────────────────────────────
    /**
     * The line the header card leaves behind when it is collapsed.
     * 🔴 It keeps the two facts that decide what EVERY panel below is showing — which
     * session, and which of its forms the ฟอร์ม / Tax JSON panels are reading — plus the
     * turn scope when the panels are pinned to a snapshot. The rest of the header is
     * counts, which a reader can go looking for; these three cannot be inferred from
     * anything else on screen, so collapsing must never hide them.
     */
    headerSummary() {
      const s = this.session || {};
      const parts = [];
      if (s.userID) parts.push(`CIF ${s.userID}`);
      if (s.sessionID) parts.push(s.sessionID);
      const form = this.selectedForm;
      if (form) parts.push(`Form #${form.formID} · ${this.statusTh(form.status)}`);
      if (this.turnSnapshotAvailable) parts.push(`แผงด้านล่าง ณ เทิร์น #${this.activeTurn.turnNo}`);
      return parts.join(' · ');
    },
    sourceTh() {
      const src = (this.session && this.session.source) || 'production';
      return src === 'playground' ? 'Tax Playground (ทดสอบ)' : 'แชทจริง (Production)';
    },
    sourceColor() {
      const src = (this.session && this.session.source) || 'production';
      return src === 'playground' ? 'purple' : 'primary';
    },
    /**
     * 🔴 Derived from the `forms[]` this page already loaded, NOT from the session
     * DTO: the Tax Log list is one row per (form × session) pair now, so
     * `formCount`/`completedCount` were dropped from that DTO — reading them here
     * would render a permanent "0 ฟอร์ม".
     */
    formCount() {
      return (this.forms || []).length;
    },
    completedFormCount() {
      return (this.forms || []).filter((f) => f && f.status === 'Completed').length;
    },
    /**
     * 🔴 เวลาที่ขึ้นบรรทัดแรกคือ **เวลาที่ฟอร์มเริ่ม** ไม่ใช่ `updatedDate`
     *
     * ของเดิมเอา `updatedDate` ขึ้นหัวแถว แล้วซ่อน "สร้าง …" ไว้บรรทัดล่าง ซึ่งใช้แยกแถวไม่ได้เลย
     * ในของจริง: ฟอร์มทั้ง session ถูกแตะพร้อม ๆ กัน (คนละฟอร์มก็ยังได้ `updatedDate` ห่างกันไม่ถึงนาที)
     * ⇒ ทุกแถวขึ้นหัวด้วยเวลาเกือบเท่ากัน อ่านแล้ว "เวลาซ้ำ ๆ กัน" ทั้งที่ฟอร์มคนละวัน
     * ตัวที่แยกแถวได้จริงคือเวลาที่ *เริ่ม* ฟอร์ม จึงย้ายขึ้นมาไว้บรรทัดแรกและติดป้ายกำกับให้ครบ
     * ทั้งสองเวลา (ของเดิมเป็นตัวเลขลอย ๆ ไม่บอกว่าเวลาอะไร)
     *
     * รูปแบบเวลาใช้ {@link fmtThaiDayTime} ไม่ใช่ `DD/MM/YYYY HH:mm` — ดูเหตุผลที่ `utils/thaiTime.js`
     */
    formItems() {
      const now = new Date();
      return (this.forms || []).map((f) => {
        // ฟอร์มที่จบแล้วเวลาที่มีความหมายคือ "จบเมื่อไร" ไม่ใช่การแตะครั้งสุดท้าย (ซึ่งอาจเป็นแค่การอ่าน)
        const done = f.status === 'Completed' && f.completedDate;
        const lastLabel = done ? 'จบ' : 'อัปเดต';
        const lastValue = done ? f.completedDate : f.updatedDate;
        const age = fmtThaiRelative(lastValue, '', now);
        return {
          title: `${f.taxText || 'Tax Plan'} · Form #${f.formID} · ${f.yearCode || f.yearID} · ${this.statusTh(f.status)} · เริ่ม ${fmtThaiDayTime(f.createdDate, '—', now)}`,
          value: f.formID,
          subtitle: `ตอบแล้ว ${f.answeredCount || 0} ข้อ · ${lastLabel} ${fmtThaiDayTime(lastValue, '—', now)}${age ? ` (${age})` : ''}`,
        };
      });
    },
    selectedForm() {
      return (this.forms || []).find((f) => f && f.formID === this.selectedFormId) || null;
    },
    /** Says out loud why there is a selector at all — a session with 32 forms is normal, not a bug. */
    formSelectHint() {
      const n = (this.forms || []).length;
      if (n > 1) return `session นี้มี ${n} ฟอร์ม (ชุดคำถามถูกเริ่มใหม่หลายรอบ) — เลือกฟอร์มที่ต้องการดู`;
      return 'เลือกฟอร์มที่ต้องการดูในแท็บ ฟอร์ม / Tax JSON';
    },
    progress() {
      return (this.formState && this.formState.progress) || null;
    },
    versions() {
      return (this.formState && this.formState.versions) || null;
    },

    // ── timeline ───────────────────────────────────────────────────────
    /** Newest first for the eye; `turns` itself stays oldest → newest for the transcript. */
    timelineDesc() {
      return (this.turns || []).slice().reverse();
    },
    hasMoreTurns() {
      return this.turns.length < (this.turnsTotal || 0);
    },
    activeTurn() {
      return (this.turns || []).find((t) => t && t.turnKey === this.activeTurnKey) || null;
    },

    // ── ฟอร์มของแต่ละเทิร์น (หัวคั่นลอยในแชท) ─────────────────────────
    /**
     * `turnKey` → `formID` ของทุกเทิร์นใน transcript — คือสิ่งที่หัวคั่นลอยในแชทใช้ตอบคำถาม
     * "บับเบิลที่กำลังอ่านอยู่นี่เป็นของฟอร์มใบไหน" (1 session มีได้ถึง 32 ฟอร์ม)
     *
     * 🔴 เทิร์น **production อนุมานจากเวลา** ไม่ใช่ค่าจริง — `dbo.Log_Message` ไม่มีคอลัมน์ FormID
     * (`TaxLogV2TurnDto.FormID` เป็น 0 ทุกแถว) ⇒ ป้ายที่ได้ต้องบอกคนอ่านว่าเป็นค่าประมาณ ดู
     * `formInferred` ที่ `bubbles()` แนบไปให้ panel · เทิร์น playground มี `formID` จริงมากับแถว
     * และ `resolveTurnFormIds` ให้ค่าจริงชนะค่าที่อนุมานเสมอ (กติกาเต็มอยู่ที่หัว
     * `utils/taxLogChatSegments.js`)
     */
    turnFormIds() {
      return resolveTurnFormIds(this.turns, this.forms);
    },
    /**
     * `formID` → ป้ายบรรทัดเดียวของหัวคั่น — สั้นไว้ก่อน เพราะมันลอยทับบทสนทนาอยู่ตลอดเวลา
     * (สถานะ/ความคืบหน้าของฟอร์มอ่านได้จากแถบสรุปกับตัวเลือกฟอร์มด้านบนอยู่แล้ว)
     */
    formTitleById() {
      const map = new Map();
      (this.forms || []).forEach((f) => {
        if (!f || !f.formID) return;
        const year = f.yearCode || f.yearID;
        map.set(f.formID, year ? `ฟอร์ม #${f.formID} · ปีภาษี ${year}` : `ฟอร์ม #${f.formID}`);
      });
      return map;
    },

    // ── chat bubbles ───────────────────────────────────────────────────
    /**
     * `userMessage` / `reply` arrive DECRYPTED and verbatim from the API (the AES
     * columns `Log_Message.TEXT` / `.replymsg` are decrypted server-side), so no
     * text is rebuilt here — an error turn simply adds a red system bubble after
     * the pair, the same way the playground renders `systemErrorText`.
     *
     * Bot bubbles additionally carry `cardText` / `cards`, the `|taxcard|` payloads the
     * chat panel renders BELOW the bubble. See the comments inside the push.
     *
     * 🔴 การ์ดขึ้น **ทุกเทิร์นที่มีการ์ด ทันที** ไม่ต้องคลิกเลือกเทิร์นก่อน และ **ห้ามลอกเงื่อนไข
     * `isActive` ของ playground มาที่นี่**: playground ต้องแยกทางเพราะ `GET /turns` ของมันคืนแค่
     * `replyPreview` (ตัด 160 ตัว) เทิร์นที่ไม่ active จึงไม่มีสายเต็มให้ดู — หน้านี้ไม่มีปัญหานั้นเลย
     * `TaxLogV2TurnDto.Reply` เป็นข้อความเต็มที่ถอดรหัสแล้วทุกแถว
     * 🔴 แต่ **การ์ด** ไม่ได้มาจาก `Reply` อีกต่อไป — มาจากฟิลด์ `taxCards` ที่ backend แยกไว้ให้ ซึ่งเป็น
     * ช่องเดียวที่ถูกต้องทั้งแถว production (Reply ดิบ) และแถว playground (Reply ถูกตัด marker แล้ว)
     * เหตุผลเต็มอยู่เหนือ `const cards` ข้างล่าง
     */
    bubbles() {
      const out = [];
      (this.turns || []).forEach((t) => {
        if (!t) return;
        const isError = this.isErrorTurn(t);
        // 🔴 ฟอร์มของเทิร์น เดินทางไปกับ **ทุกบับเบิล** ของเทิร์นนั้น (ผู้ใช้ / บอท / ระบบ) เพราะ panel
        //    ยุบหัวคั่นจากลำดับบับเบิล ไม่ใช่จากลำดับเทิร์น — บับเบิลที่ไม่มีฟอร์มติดมาจะตัดช่วงผิดจุด
        const formID = this.turnFormIds.get(t.turnKey) || 0;
        const seg = {
          formID,
          formTitle: formID
            ? (this.formTitleById.get(formID) || `ฟอร์ม #${formID}`)
            : 'ยังไม่เข้าฟอร์มภาษี',
          // true = ป้ายนี้เดาจากเวลา (แถว production) · false = แถวนั้นบอก formID ของตัวเองมาจริง (playground)
          formInferred: formID > 0 && !(Number(t.formID) > 0),
        };
        if (t.userMessage) {
          out.push({
            key: `u-${t.turnKey}`,
            side: 'user',
            text: t.userMessage,
            time: t.createdDate,
            turnNo: t.turnNo,
            turnKey: t.turnKey,
            isError: false,
            ...seg,
          });
        }
        // 🔴 การ์ดอ่านจากฟิลด์ `t.taxCards` **เท่านั้น** ห้ามแยกเองจาก `t.reply` (doc ของ
        //    `TaxLogV2TurnDto.TaxCards` เขียนกำกับไว้ตรง ๆ ว่า "ช่องเดียวที่เชื่อถือได้ทั้งสองแหล่ง"):
        //    `Reply` ไม่สมมาตรโดยตั้งใจ — production = สายดิบ marker ครบ · playground = backend ตัด marker
        //    ทิ้งไปแล้วตั้งแต่ mapper เพราะ payload 2 KB กลบประโยคที่บอทพูดจริงใน transcript ⇒ `splitTaxCards`
        //    เจอการ์ดเฉพาะแถว production ส่วนเทิร์น playground ไม่มีการ์ดขึ้นเลยทั้งที่ payload อยู่ใน DB
        //    (นั่นคือบั๊กที่ฟิลด์ `taxCards` มาแก้ ทั้งสองแหล่งได้ค่าจาก `TaxCardMarkerV2.SplitMarker(...).payload`
        //    ตัวเดียวกัน จึงเป็นการ์ดชุดเดียวกันเป๊ะ)
        // 🔴 `parseTaxCardsField` คืน **doc ล้วน ๆ** ไม่ใช่ `{ raw, doc }` ที่ panel/`TaxSummaryCardV2` ต้องการ
        //    จึงต้อง map เอง และ `raw` ถูก re-serialise (เหมือนที่ `splitTaxCards` ทำกับ payload รูป legacy array)
        //    เพราะฟิลด์นี้ไม่ได้เก็บ source span ของการ์ดแต่ละใบไว้ — string นี้ใช้แค่ในตัวอ่าน JSON ของการ์ด
        // 🔴 backend รุ่นก่อนที่ยังไม่ส่ง `taxCards` ⇒ `t.taxCards` เป็น undefined ⇒ `parseTaxCardsField` คืน `[]`
        //    (guard `json == null` ตัวแรกของมัน) ไม่ throw — เทิร์นนั้นแค่ไม่มีการ์ดขึ้น เหมือนเทิร์นธรรมดา
        // ⚠️ ผลข้างเคียงที่รู้ตัว: payload ที่ balanced แต่ JSON ไม่ถูก ทำให้ทั้ง array string พังทั้งอัน
        //    ⇒ `parseTaxCardsField` คืน `[]` ⇒ ไม่มี v-alert แดง "อ่าน payload ไม่ได้" ให้เห็นในเทิร์นนั้น
        //    หลักฐานยังไม่หาย: แถว production ยังพลิกดูสายดิบได้ด้วยสวิตช์ "ดู marker ดิบ" (สายดิบ ≠ สายที่ถอดแล้ว
        //    ⇒ สวิตช์โผล่) — เทียบกับการ split เองซึ่งทำให้เทิร์น playground ไม่เห็นการ์ดเลย อันนี้แลกแล้วคุ้มกว่า
        const cards = parseTaxCardsField(t.taxCards).map((doc) => ({ raw: JSON.stringify(doc), doc }));
        // 🔴 เงื่อนไขคือ "มีข้อความ **หรือ** มีการ์ด" ไม่ใช่ `if (t.reply)` เฉย ๆ (หลักเดียวกับ `TaxPlaygroundV2.vue`):
        //    CARD-ONLY turn (lead text ว่าง + marker — รูปที่ node รองรับโดยตั้งใจ เพราะการ์ดส่วนใหญ่มีหัวข้อของ
        //    ตัวเองอยู่แล้ว) มาถึงที่นี่ฝั่ง playground ด้วย `reply === ''` เพราะ marker ถูกตัดออกจาก transcript
        //    ไปแล้ว ⇒ เช็คแค่ `t.reply` จะข้าม bot bubble ทั้งอัน แล้วการ์ดหายทั้งที่ payload มาถึงแล้ว
        //    (ฟองเทาว่าง ๆ ไม่เกิด: panel กันไว้ที่ `showBubble()` ของมันอยู่แล้ว)
        if (t.reply || cards.length) {
          out.push({
            key: `b-${t.turnKey}`,
            side: 'bot',
            // 🔴 `text` ยัง RAW — marker และทุกอย่างครบ หน้านี้คือที่ที่คนมาตรวจ wire format ที่ระบบส่งออกจริง
            // การ์ดเป็น "ของเพิ่ม" ไม่ใช่ "ของแทน" (หลักเดียวกับ `TaxPlaygroundV2.vue` → `bubbles()`)
            text: t.reply,
            // สำเนาที่อ่านง่ายวิ่งคู่ไปด้วย: panel เอาไปแสดงในฟองแทน `text` โดยดีฟอลต์ แล้วสวิตช์ "ดู marker ดิบ"
            // ของมันพลิกกลับมาใช้ `text` เมื่อต้องการตรวจสาย
            // 🔴 สูตรเดียวใช้ได้ทั้งสองแหล่ง: production สาย ดิบ ⇒ ตัด marker block ออก (คำถามที่อยู่ **หลัง**
            //    การ์ดยังอยู่ครบ เพราะ `splitTaxCards` ต่อชิ้นหน้า+หลัง marker กลับเข้าด้วยกัน ไม่ใช่ `split()[0]`)
            //    · playground ไม่มี marker ในสายอยู่แล้ว ⇒ `splitTaxCards` คืนสายเดิมทั้งดุ้น (ไม่ trim ด้วยซ้ำ) = no-op
            // 🔴 ไม่แขวนกับ `cards.length` อีกต่อไป: เทิร์นที่ไม่มีการ์ดจะได้ `cardText === text` เป๊ะ ๆ
            //    ⇒ ฟองแสดงเหมือนเดิมทุกตัวอักษร และ `hasRawWire` ของ panel (เทียบสองค่านี้) รู้เองว่าไม่มีสายดิบให้ดู
            cardText: splitTaxCards(t.reply).text,
            // `{ raw, doc }[]` — panel ต้องการรูปนี้ (`doc` ป้อน `TaxSummaryCardV2`, `raw` ป้อนตัวอ่าน JSON)
            cards,
            // 🔴 ยังไม่มี `cardsDiffer` ที่นี่ และห้ามใส่: ตอนนี้ FE ไม่ได้แยกการ์ดเองแล้ว การ์ดมาจาก `taxCards`
            //    ทางเดียว จึงไม่มี "ความเห็นที่สอง" ให้เทียบ (playground มีได้เพราะเทิร์น active ของมัน split
            //    สายเต็มเองคู่ไปกับฟิลด์) ใส่ไปจะได้ธงส้มทุกแถวโดยไม่มีอะไรผิดจริง
            time: t.createdDate,
            turnNo: t.turnNo,
            turnKey: t.turnKey,
            isError,
            ...seg,
          });
        }
        if (isError && t.errorMessage) {
          out.push({
            key: `e-${t.turnKey}`,
            side: 'system',
            text: `เทิร์นนี้ผิดพลาด: ${t.errorMessage}`,
            time: t.createdDate,
            turnNo: t.turnNo,
            turnKey: t.turnKey,
            isError: true,
            ...seg,
          });
        }
      });
      return out;
    },

    // ── inspector · ฟอร์ม / Tax JSON ────────────────────────────────────
    /**
     * 🔴 Note 3 — only a playground turn carries its own snapshot. Everything else
     * falls back to the CURRENT state of the selected form, and the panel says so.
     */
    turnSnapshotAvailable() {
      const t = this.activeTurn;
      return Boolean(t && t.source === 'playground' && t.hasSnapshot && this.turnDetail);
    },
    configFormIsSnapshot() {
      return this.turnSnapshotAvailable && Boolean(this.turnDetail.configForm);
    },
    taxStateIsSnapshot() {
      return this.turnSnapshotAvailable && Boolean(this.turnDetail.taxState);
    },
    configForm() {
      if (this.configFormIsSnapshot) return this.turnDetail.configForm;
      return (this.formState && this.formState.configForm) || null;
    },
    /** Highlighted rows only exist for a playground snapshot; the live form has no "this turn". */
    changedPaths() {
      return this.configFormIsSnapshot ? (this.turnDetail.changedPaths || []) : [];
    },
    configFormScopeTh() {
      if (this.configFormIsSnapshot) {
        return `สถานะฟอร์ม ณ เทิร์นนี้ (เทิร์น #${this.activeTurn.turnNo}) — บันทึกไว้ตอนทดสอบใน Tax Playground`;
      }
      const formPart = this.selectedFormId ? ` — ฟอร์ม #${this.selectedFormId}` : '';
      return `สถานะฟอร์ม ณ ปัจจุบัน (ไม่ใช่ ณ เทิร์นที่เลือก)${formPart}`;
    },
    /**
     * 🔴 The panel's OWN header used to hard-code "Form Template (ณ turn นี้)",
     * which sat directly under the alert above saying "ณ ปัจจุบัน (ไม่ใช่ ณ
     * เทิร์นที่เลือก)" — two contradictory scope claims, on every production
     * session (the common case). These two strings are what the page hands the
     * shared panel; the panel's defaults stay the playground's wording.
     */
    configFormPanelScopeLabel() {
      return this.configFormIsSnapshot
        ? `Form Template (ณ เทิร์น #${this.activeTurn.turnNo})`
        : 'Form Template (ณ ปัจจุบัน)';
    },
    configFormPanelScopeHint() {
      return this.configFormIsSnapshot
        ? 'ทั้งสอง sync กันทุก turn.'
        : 'ทั้งสองแสดงสถานะ ณ ปัจจุบัน ไม่ใช่ ณ เทิร์นที่เลือก (แชทจริงไม่ได้เก็บสแนปช็อตรายเทิร์น).';
    },
    /**
     * 🔴 Suffix for the HEADER chips. They always read `formState` (current
     * state); when the panels below have switched to a turn snapshot the two
     * disagree on purpose, and the chip must carry the scope in its own text.
     */
    headerScopeSuffix() {
      return this.turnSnapshotAvailable ? ' ณ ปัจจุบัน' : '';
    },
    taxStateScopeTh() {
      if (this.taxStateIsSnapshot) {
        return `Tax JSON ณ เทิร์นนี้ (เทิร์น #${this.activeTurn.turnNo})`;
      }
      const formPart = this.selectedFormId ? ` — ฟอร์ม #${this.selectedFormId}` : '';
      return `Tax JSON ณ ปัจจุบัน (ไม่ใช่ ณ เทิร์นที่เลือก)${formPart}`;
    },
    taxStateObject() {
      if (this.taxStateIsSnapshot) return this.turnDetail.taxState;
      return (this.formState && this.formState.taxState) || null;
    },
    taxJsonCompact() {
      return this.taxStateObject ? JSON.stringify(this.taxStateObject) : '';
    },
    taxJsonKb() {
      return Math.round(this.taxJsonCompact.length / 1024);
    },
    taxJsonTooBig() {
      return this.taxJsonCompact.length > JSON_BIG_BYTES && !this.taxJsonShowAll;
    },
    taxStateText() {
      if (!this.taxStateObject) return 'ยังไม่มีข้อมูลฟอร์มของ session นี้';
      if (this.taxJsonTooBig) return this.taxJsonCompact;
      return JSON.stringify(this.taxStateObject, null, 2);
    },

    // ── inspector · Prompt & LLM ───────────────────────────────────────
    llmCalls() {
      return (this.turnDetail && this.turnDetail.llmCalls) || [];
    },
    /** 🔴 L6 twin — a failed call is `statusReturn != 0` (or an `error`), never the turn status. */
    llmFailedCount() {
      return this.llmCalls.filter((c) => (c.statusReturn ?? 0) !== 0 || Boolean(c.error)).length;
    },
    /** Same number the playground computes — it drives the viewer's ">80 refs" warning. */
    askableCount() {
      const groups = (this.configForm && this.configForm.groups) || [];
      return groups.reduce((n, g) => n + ((g && g.fields) || []).length, 0);
    },
    /** Where these prompts were read from — the two sources look different on screen. */
    llmSourceTh() {
      const t = this.activeTurn;
      if (t && t.source === 'playground') {
        return this.isOffline
          ? 'ที่มา: สแนปช็อตของ Tax Playground (มีข้อมูลชั้น prompt ครบ) — อ่านจากไฟล์ export จึงเปิดแก้คอนฟิกจากหน้านี้ไม่ได้'
          : 'ที่มา: สแนปช็อตของ Tax Playground (มีข้อมูลชั้น prompt ครบ)';
      }
      const prod = 'ที่มา: log.Log_Monitor ของแชทจริง — ชั้น prompt แยกจากตัวข้อความที่ส่งจริง '
           + 'จึงครบทุกชั้นแต่ไม่มีที่มาของแต่ละชั้น (แก้คอนฟิกจากหน้านี้ไม่ได้ ต้องดูที่ Playground)';
      return this.isOffline ? `${prod} — อ่านจากไฟล์ export` : prod;
    },

    // ── inspector · Trace ──────────────────────────────────────────────
    traceText() {
      if (!this.activeTurn) return 'ยังไม่ได้เลือกเทิร์น';
      return (this.turnDetail && this.turnDetail.trace) || 'ไม่มี trace ของเทิร์นนี้';
    },
    /** Production only — decrypted `ChatWorkFlow` steps of `dbo.Log_Message`. */
    steps() {
      return (this.turnDetail && this.turnDetail.steps) || [];
    },

    // ── inspector · Flow ───────────────────────────────────────────────
    /**
     * 🔴 The one turn-detail block that BOTH sources carry, so it must NOT branch on
     * `turn.source` the way the panels above it do: production reads it back from
     * `WorkflowStep.Output.TaxTurnFlow` of the `taxPlanningV2` step, the playground from
     * its own `FlowJson` column, and the API projects both into the same `flow` key.
     * (Contrast: `configForm`/`taxState` are playground-only, `steps` is production-only.)
     *
     * `null` here is NOT one fact — it can mean "no step recorded", "turn predates the
     * feature" or "the row hit its 1 MB cap and `flow` was shed". Telling those apart is
     * the panel's job, which is why it also gets `source` + `traceText`.
     */
    turnFlow() {
      return (this.turnDetail && this.turnDetail.flow) || null;
    },
    /**
     * 🔴 NOT `turnFlow != null`. "ยังไม่ได้เลือกเทิร์น" and "เลือกแล้ว แต่เทิร์นนั้นไม่มี flow"
     * are two different screens, and collapsing them makes an unselected inspector accuse
     * every turn of having no flow.
     */
    turnSelected() {
      return Boolean(this.activeTurn);
    },
    /**
     * 🔴 Must be exactly `'production'` on a real chat turn: it is what STOPS the panel
     * from reading the "flow ถูกตัด" line out of `trace`. That guard line is written only
     * on the playground persistence path, so honouring it on a production trace would be
     * believing text that can only have come from somewhere else.
     */
    turnSource() {
      return (this.activeTurn && this.activeTurn.source) || '';
    },
    /**
     * Tab badge — the `llmFailedCount` rule applied to flow: only a REFUSED or FAILED step
     * earns the orange. `skipped`/`noop` are the normal texture of a turn (a walk skips
     * dozens of items every pass), so counting them would leave the tab permanently alarmed.
     */
    flowProblemCount() {
      const steps = (this.turnFlow && this.turnFlow.steps) || [];
      return steps.filter((s) => s && (s.outcome === 'rejected' || s.outcome === 'error')).length;
    },

    // ── tabs ───────────────────────────────────────────────────────────
    tabDefs() {
      const llm = {
        value: 'llm',
        label: `Prompt & LLM (${this.llmCalls.length})`,
        color: this.llmFailedCount > 0 ? 'orange' : undefined,
        icon: this.llmFailedCount > 0 ? 'mdi-alert' : null,
      };
      // Label stays the bare word "Flow" — no step count. The strip already carries
      // "Prompt & LLM (N)" and this column is narrow; a second number is what pushes it
      // into `show-arrows` territory. The alert icon appears only when something is wrong.
      const flow = {
        value: 'flow',
        label: 'Flow',
        color: this.flowProblemCount > 0 ? 'orange' : undefined,
        icon: this.flowProblemCount > 0 ? 'mdi-alert' : null,
      };
      const base = [
        { value: 'form', label: 'ฟอร์ม' },
        llm,
        { value: 'json', label: 'Tax JSON' },
        flow,
        { value: 'trace', label: 'Trace' },
      ];
      return this.isXs ? [{ value: 'chat', label: 'แชท' }].concat(base) : base;
    },
    /** Falls back to the first tab of the CURRENT breakpoint, so resizing cannot strand it. */
    activeTab() {
      const defs = this.tabDefs;
      const hit = defs.find((t) => t.value === this.inspectorTab);
      return hit ? hit.value : (defs[0] && defs[0].value);
    },

    // ── grid ───────────────────────────────────────────────────────────
    /** Same spans as the playground: the inspector is the widest column from md up. */
    colSpans() {
      const chat = { cols: 12, sm: 6, md: 5, lg: 4 };
      if (this.inspectorFull) {
        return {
          timeline: { cols: 'auto', md: 'auto', lg: 'auto' },
          chat,
          inspector: { cols: 12, sm: 12, md: 12, lg: 12 },
        };
      }
      if (this.timelineCollapsed) {
        return {
          timeline: { cols: 'auto', md: 'auto', lg: 'auto' },
          chat,
          inspector: { cols: 12, sm: 6, md: 6, lg: 7 },
        };
      }
      return {
        timeline: { cols: 12, md: 2, lg: 2 },
        chat,
        inspector: { cols: 12, sm: 6, md: 5, lg: 6 },
      };
    },
  },
  watch: {
    /**
     * Same component, different data: another session (list → detail → back → another
     * row), a file picked / closed on the offline route, or live ↔ offline.
     * 🔴 Watches `dataSourceKey`, NOT `sessionId` alone: two exports of the same session
     * share a `sessionId`, so "เปิดไฟล์อื่น" → the newer export would never reload.
     */
    dataSourceKey() {
      this.loadAll();
    },
  },
  /**
   * 🔴 `created`, not `mounted` — the header must not render open for one frame and
   * then snap shut on xs. Same default as the playground's control bar: collapsed on
   * xs, open from sm up.
   */
  created() {
    this.headerOpen = !this.isXs;
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    // ── loading ───────────────────────────────────────────────────────
    async loadAll() {
      this.resetState();
      // 🔴 Offline with no file yet: nothing to read, and NOT "session not found" —
      // `loadSession` would read the empty `sessionId` as a 404 and draw the
      // not-found screen under the picker. The picker is the whole page here.
      if (!this.api) return;
      await this.loadSession();
      if (this.sessionNotFound || this.sessionError) return;
      // The transcript and the form panels are independent reads — fire both.
      await Promise.all([this.loadTurns(), this.loadFormState()]);
      await this.applyDeepLink();
    },
    resetState() {
      this.session = null;
      this.forms = [];
      this.selectedFormId = 0;
      this.formState = null;
      this.turns = [];
      this.turnsTotal = 0;
      this.turnsPage = 1;
      this.activeTurnKey = null;
      this.turnDetail = null;
      this.sessionNotFound = false;
      this.sessionError = null;
      this.turnsError = null;
      this.turnError = null;
      this.formError = null;
      this.taxJsonShowAll = false;
    },
    async loadSession() {
      const api = this.api;
      if (!api) return;
      if (!this.sessionId) {
        this.sessionNotFound = true;
        return;
      }
      this.loadingSession = true;
      this.sessionError = null;
      this.sessionNotFound = false;
      try {
        const data = await api.getSession(this.sessionId);
        this.session = (data && data.session) || null;
        this.forms = (data && data.forms) || [];
        // `forms` arrives NEWEST first — the newest form is what an admin means by
        // "this session's form" almost every time.
        // 🔴 …except when they got here from the Tax Log list, where a row IS a
        // (form × session) pair and says which form it means via `?form=`. That
        // wins; anything else (absent, junk, or a form this session never
        // touched — e.g. a link pasted onto the wrong session) falls back to the
        // newest SILENTLY, because a deep link that half-works is still a
        // read-only view of the right session.
        this.selectedFormId =
          this.deepLinkFormId() || (this.forms[0] && this.forms[0].formID) || 0;
      } catch (err) {
        if (err && err.httpStatus === 404) this.sessionNotFound = true;
        else this.sessionError = (err && err.message) || 'โหลดข้อมูล session ไม่สำเร็จ';
      } finally {
        this.loadingSession = false;
      }
    },
    async loadTurns() {
      const api = this.api;
      if (!api || !this.sessionId) return;
      this.loadingTurns = true;
      this.turnsError = null;
      try {
        const res = await api.listTurns(this.sessionId, 1, TURN_PAGE_SIZE);
        this.turns = (res && res.items) || [];
        this.turnsTotal = (res && res.totalCount) || this.turns.length;
        this.turnsPage = 1;
      } catch (err) {
        this.turnsError = (err && err.message) || 'โหลดไทม์ไลน์ไม่สำเร็จ';
      } finally {
        this.loadingTurns = false;
      }
    },
    /** Appends the next page — the transcript must stay oldest → newest, so never prepend. */
    async loadMoreTurns() {
      const api = this.api;
      if (!api || !this.hasMoreTurns || this.loadingTurns) return;
      this.loadingTurns = true;
      this.turnsError = null;
      const next = this.turnsPage + 1;
      try {
        const res = await api.listTurns(this.sessionId, next, TURN_PAGE_SIZE);
        const items = (res && res.items) || [];
        const seen = new Set(this.turns.map((t) => t && t.turnKey));
        this.turns = this.turns.concat(items.filter((t) => t && !seen.has(t.turnKey)));
        this.turnsTotal = (res && res.totalCount) || this.turnsTotal;
        this.turnsPage = next;
      } catch (err) {
        this.turnsError = (err && err.message) || 'โหลดเทิร์นเพิ่มไม่สำเร็จ';
      } finally {
        this.loadingTurns = false;
      }
    },
    async loadFormState() {
      // 🔴 DROP THE OLD FORM FIRST. `onSelectForm` moves `selectedFormId`
      // synchronously, so the scope labels (`— ฟอร์ม #456`) flip immediately while
      // this request is in flight. Keeping `formState` would leave the ฟอร์ม /
      // Tax JSON panes serialising form #123 under form #456's heading for the
      // whole round trip — and the header chips stale in the same window.
      this.formState = null;
      this.formError = null;
      this.taxJsonShowAll = false;
      const api = this.api;
      if (!api || !this.selectedFormId) return;
      this.loadingForm = true;
      const requested = this.selectedFormId;
      try {
        const data = await api.getFormState(requested);
        // The admin may have switched forms while this was in flight — a stale
        // answer must never overwrite the newer one.
        if (requested !== this.selectedFormId) return;
        this.formState = data || null;
        this.taxJsonShowAll = false;
      } catch (err) {
        if (requested !== this.selectedFormId) return;
        this.formState = null;
        this.formError = (err && err.message) || 'โหลดสถานะฟอร์มไม่สำเร็จ';
      } finally {
        if (requested === this.selectedFormId) this.loadingForm = false;
      }
    },
    /** 🔴 Local view state ONLY — picking a form reads `GET /forms/{id}` and writes nothing. */
    onSelectForm(formId) {
      this.selectedFormId = Number(formId) || 0;
      this.syncDeepLinkQuery();
      this.loadFormState();
    },
    /**
     * `?form=<id>` → the form of THIS session it names, else 0.
     * Requires `forms[]` to be loaded, so it is only ever called after
     * `getSession()` has answered.
     */
    deepLinkFormId() {
      const raw = (this.$route.query || {}).form;
      const id = Number(Array.isArray(raw) ? raw[0] : raw);
      if (!Number.isFinite(id) || id <= 0) return 0;
      return (this.forms || []).some((f) => f && f.formID === id) ? id : 0;
    },
    /**
     * The page's ENTIRE deep-link surface is two keys — `?form=` (which form the
     * ฟอร์ม / Tax JSON panels read) and `?turn=` (which turn is pinned) — and this
     * is the only place either is written. Both are re-derived from component
     * state on every call and merged onto whatever else the query holds.
     *
     * 🔴 Why one writer and not one per key: `$route` does NOT update
     * synchronously after `$router.replace`, so two merge-writes in the same tick
     * would each read the PRE-replace query and the second would silently undo the
     * first. Deriving both keys from state on every write makes that ordering
     * harmless. The old `syncTurnQuery` wrote `{ query: { turn } }` wholesale,
     * which erased the `?form=` the Tax Log list had just handed over — the first
     * turn a reader clicked snapped the panels back to `forms[0]`.
     *
     * @param {Object} [opts] `{ keepTurnQuery: true }` — during `applyDeepLink`
     *   the `?turn=` in the URL has not been resolved to a turn yet and
     *   `activeTurn` is still null, so pass it through untouched instead of
     *   deriving (and thereby deleting) it.
     */
    syncDeepLinkQuery(opts) {
      const current = this.$route.query || {};
      const query = { ...current };

      if (this.selectedFormId) query.form = String(this.selectedFormId);
      else delete query.form;

      if (!(opts && opts.keepTurnQuery)) {
        const turn = this.activeTurn;
        if (turn) query.turn = String(turn.turnNo);
        else delete query.turn;
      }

      // Skip identical navigations (Vue Router warns on duplicates).
      const keys = Object.keys(query);
      const same =
        keys.length === Object.keys(current).length
        && keys.every((k) => String(current[k]) === String(query[k]));
      if (same) return;
      this.$router.replace({ query }).catch(() => {
        /* redundant navigation — harmless */
      });
    },

    // ── turn selection ────────────────────────────────────────────────
    /**
     * 🔴 Note 4 — `turnKey` selects, `source` decides the endpoint, and the
     * numeric id is what actually travels.
     */
    async onOpenTurn(turnKey) {
      if (!turnKey) return;
      this.activeTurnKey = turnKey;
      // 🔴 Move the form selection FIRST, then write the URL once — `syncDeepLinkQuery`
      // publishes both keys together, so `followTurnForm` must not write its own.
      this.followTurnForm();
      this.syncDeepLinkQuery();
      await this.loadTurnDetail();
    },
    /**
     * A playground turn names the form it belongs to (`formID`); a production turn
     * cannot (`0` — `Log_Message` has no form column). When it does, and that form
     * is one of this session's, point the ฟอร์ม / Tax JSON panels at it so the
     * three panels never describe two different forms. Still read-only: this only
     * moves the local selection and re-reads `GET /forms/{id}`.
     */
    followTurnForm() {
      const t = this.activeTurn;
      const formId = (t && t.formID) || 0;
      if (!formId || formId === this.selectedFormId) return;
      if (!(this.forms || []).some((f) => f && f.formID === formId)) return;
      this.selectedFormId = formId;
      // 🔴 No URL write here — the caller (`onOpenTurn`) publishes `?form=` and
      // `?turn=` in ONE `syncDeepLinkQuery()` right after this returns.
      this.loadFormState();
    },
    async loadTurnDetail() {
      const turn = this.activeTurn;
      const api = this.api;
      if (!turn || !api) {
        this.turnDetail = null;
        return;
      }
      this.loadingTurn = true;
      this.turnError = null;
      const key = turn.turnKey;
      try {
        const detail = turn.source === 'playground'
          ? await api.getPlaygroundTurn(turn.playgroundTurnID)
          : await api.getProductionTurn(turn.logMessageID);
        if (key !== this.activeTurnKey) return;
        this.turnDetail = detail || null;
        this.taxJsonShowAll = false;
      } catch (err) {
        if (key !== this.activeTurnKey) return;
        this.turnDetail = null;
        // 🔴 A 404 means different things per source: live = deleted / another
        // company's row; offline = the export skipped this turn (see the file's
        // `warnings`). The live wording would send the reader hunting for a
        // tenant problem that does not exist.
        const offline404 = this.isOffline || Boolean(err && err.offline);
        let notFound = 'ไม่พบรายละเอียดของเทิร์นนี้ — อาจถูกลบ หรือเป็นของบริษัทอื่น';
        if (offline404) notFound = 'ไม่มีรายละเอียดเทิร์นนี้ในไฟล์';
        this.turnError = err && err.httpStatus === 404
          ? notFound
          : ((err && err.message) || 'โหลดรายละเอียดเทิร์นไม่สำเร็จ');
      } finally {
        if (key === this.activeTurnKey) this.loadingTurn = false;
      }
    },
    reloadActiveTurn() {
      this.loadTurnDetail();
    },
    isActiveTurn(turnKey) {
      return Boolean(turnKey && turnKey === this.activeTurnKey);
    },
    isErrorTurn(t) {
      return Boolean(t && t.status === 'Error');
    },
    /**
     * ป้ายบรรทัดเดียวของเทิร์น ใช้ทั้งใน timeline (`txl-turn-card-*`) และ strip.
     *
     * 🔴 ถอด `|taxcard|` ออกก่อนเสมอ: ฝั่งแชทแสดงเทิร์นการ์ดด้วยสำเนาที่ถอด marker แล้ว
     *    (`bubbles()` → `cardText`) ป้ายที่ยังพ่นสายดิบทำให้หน้าเดียวกันพูดคนละภาษา — เทิร์นที่
     *    ไม่มี `userMessage` (bot-only / card-only) เคยได้ป้ายเป็น
     *    `|taxcard|({"type":"TAX_INCOME_BREAKDOWN"…` ตัดปลาย ซึ่งบอกไม่ได้ด้วยซ้ำว่าการ์ดใบไหน
     *    ⚠️ เทิร์นที่ไม่มีการ์ด `splitTaxCards` คืน `text` เท่ากับสายเดิมทั้งดุ้น (ไม่ trim ด้วยซ้ำ)
     *    พฤติกรรมของแถวปกติจึงไม่เปลี่ยนเลยแม้แต่ตัวเดียว
     * 🔴 CARD-ONLY turn (ทั้งข้อความคือ marker) ถอดแล้วเหลือ `''` — ตรงนี้ **ไม่** ตกกลับไปใช้สายดิบ
     *    เพราะสายดิบคือสิ่งที่กำลังหนีอยู่ และป้ายว่าง/`—` ก็อ่านไม่ออกว่าเทิร์นนั้นคืออะไร จึงใช้ป้าย
     *    บรรยายสั้น ๆ ในวงเล็บเหลี่ยมแทน (วงเล็บเหลี่ยม = จงใจให้อ่านออกว่านี่ไม่ใช่ข้อความจริงของเทิร์น)
     *    นับใบด้วย เพราะเทิร์นที่ข้ามคำถาม SUMMARY สองข้อส่ง marker สองรอบได้ (TaxCardMarkerV2.cs)
     * 🔴 `t.reply` เป็น null/undefined ได้ — `splitTaxCards` รับแล้วคืน `{ text: '', cards: [] }`
     *    (utils/taxCardMarker.js บรรทัดแรกของฟังก์ชัน) ไม่ throw · fallback สายดิบยังคงไว้เป็นด่าน
     *    สุดท้ายสำหรับเคสอย่าง `|taxcard|([])` ที่กิน marker ไปแล้วแต่ได้ 0 ใบ: ยังดีกว่าปล่อยเป็น `—`
     * 🔴 จำนวนใบนับจาก `t.taxCards` (ช่องเดียวกับที่ `bubbles()` ใช้) **ไม่ใช่** จาก `split.cards`:
     *    แถว playground ไม่มี marker ในสายเลย (backend ตัดให้แล้ว) `split.cards` จึงเป็น `[]` เสมอ และ
     *    CARD-ONLY turn ของ playground เคยได้ป้าย `—` ทั้งที่เทิร์นนั้นส่งการ์ดจริง ๆ · ยังนับ `split.cards`
     *    ต่อท้ายไว้ให้แถว production ที่ payload พังจน `taxCards` parse ไม่ผ่าน (ป้ายยังบอกได้ว่ามีการ์ด)
     */
    turnPreview(t) {
      if (!t) return '—';
      const oneLine = (v) => (v == null ? '' : String(v)).replace(/\s+/g, ' ').trim();
      if (t.userMessage) return oneLine(t.userMessage) || '—';
      const split = splitTaxCards(t.reply);
      const stripped = oneLine(split.text);
      if (stripped) return stripped;
      const n = parseTaxCardsField(t.taxCards).length || (split.cards || []).length;
      if (n) return n > 1 ? `[การ์ดสรุปภาษี ${n} ใบ]` : '[การ์ดสรุปภาษี]';
      return oneLine(t.reply) || '—';
    },

    // ── deep link / clipboard ─────────────────────────────────────────
    /**
     * 🔴 The link carries `turnNo`, NOT `turnKey`: `turnNo` is 1-based within the
     * session's own ordering and page-independent, so the URL survives paging and
     * stays readable. A `?turn=` that no longer resolves is stripped (never an
     * error), the same rule the playground follows.
     *
     * 🔴 TWO independent deep-link keys live in this query string: `?form=`
     * (which form the ฟอร์ม / Tax JSON panels read — applied in `loadSession()`,
     * where `forms[]` finally exists) and `?turn=` (which turn is pinned).
     * Neither may clobber the other, which is why every write below merges.
     */
    async applyDeepLink() {
      // `?form=` was consumed by `loadSession()`; re-assert it so a junk or
      // foreign id disappears from the URL instead of lingering as a lie about
      // what the panels are showing. `keepTurnQuery` because `?turn=` has not been
      // resolved yet — deriving it here would delete it before we look it up.
      this.syncDeepLinkQuery({ keepTurnQuery: true });
      const raw = (this.$route.query || {}).turn;
      if (!raw) return;
      const turnNo = Number(raw);
      let hit = this.findTurnByNo(turnNo);
      // 🔴 `loadAll` has only page 1 (TURN_PAGE_SIZE rows) in `turns`, but
      // `copyTurnLink` mints a `?turn=` for ANY loaded turn — so a link to turn
      // #250 of a 300-turn session used to be declared missing and STRIPPED.
      // Page forward (bounded by `hasMoreTurns`, and by a page that added
      // nothing, which is how a failed `loadMoreTurns` shows up) before giving up.
      while (
        !hit
        && Number.isFinite(turnNo)
        && turnNo > 0
        && turnNo <= (this.turnsTotal || 0)
        && this.hasMoreTurns
      ) {
        const before = this.turns.length;
        // eslint-disable-next-line no-await-in-loop
        await this.loadMoreTurns();
        if (this.turns.length === before) break;
        hit = this.findTurnByNo(turnNo);
      }
      if (hit) {
        this.onOpenTurn(hit.turnKey);
        return;
      }
      this.showSnack(`ไม่พบเทิร์น #${raw} ใน session นี้`, 3000, 'warning');
      // 🔴 Strips ONLY the dead `turn` (nothing was opened, so `activeTurn` is
      // null). The old `{ query: {} }` here also threw away a perfectly good
      // `?form=`, silently snapping the panels back to the newest form.
      this.syncDeepLinkQuery();
    },
    findTurnByNo(turnNo) {
      return (this.turns || []).find((t) => t && t.turnNo === turnNo) || null;
    },
    copyTurnLink() {
      const turn = this.activeTurn;
      if (!turn) return;
      // Carry the form too, or the link reopens the turn with the panels pointed
      // at the newest form instead of the one on screen.
      const form = this.selectedFormId ? `&form=${this.selectedFormId}` : '';
      const url = `${window.location.origin}/TaxLogV2/${encodeURIComponent(this.sessionId)}?turn=${turn.turnNo}${form}`;
      this.onCopy({ text: url, label: 'ลิงก์เทิร์น' });
    },
    copySessionLink() {
      const url = `${window.location.origin}/TaxLogV2/${encodeURIComponent(this.sessionId)}`;
      this.onCopy({ text: url, label: 'ลิงก์ session' });
    },

    // ── export / offline file ─────────────────────────────────────────
    /**
     * "ส่งออก JSON" — the whole session (every turn's detail + every form's state) in ONE
     * file that `/TaxLogV2/offline` opens back. Live only; still a GET.
     *
     * 🔴 The endpoint answers with a normal `ResponseModel`, not a file, so the download is
     * built HERE from a Blob — the same pattern as every Tax V2 export
     * (`TaxConfigurationV2.vue → onExportQuestionVersion`). A long session exports at tens of
     * MB (full prompts + full LLM replies per call), which is also why the file is never
     * kept in web storage on the way back in.
     * 🔴 The object URL is revoked on the NEXT task: Safari cancels an in-flight download
     * when its blob URL disappears in the same task.
     */
    async exportJson() {
      if (this.isOffline || this.exportLoading || !this.sessionId) return;
      this.exportLoading = true;
      try {
        const data = await taxLogV2Api.exportSession(this.sessionId);
        const fileName = buildTaxLogExportFileName(data);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
        // Counted from the arrays actually in the file, not from `source.*`.
        const info = describeTaxLogExport(data);
        let message = `ส่งออก ${info.turnCount.toLocaleString()} เทิร์น / ${info.formCount.toLocaleString()} ฟอร์ม แล้ว`;
        if (info.warnings.length) message += ` (มี ${info.warnings.length} คำเตือน — ดูใน warnings ของไฟล์)`;
        this.showSnack(message, 4000, 'success');
      } catch (err) {
        const message = err && err.httpStatus === 404
          ? 'ไม่พบ session นี้'
          : ((err && err.message) || 'ส่งออก JSON ไม่สำเร็จ');
        this.showSnack(message, 6000, 'error');
      } finally {
        this.exportLoading = false;
      }
    },
    pickOfflineFile() {
      const el = this.$refs.offlineFileInput;
      // ล้างก่อน เพื่อให้เลือกไฟล์เดิมซ้ำ (เช่น หลังแก้ไฟล์ที่เปิดไม่ผ่าน) แล้ว `change` ยังยิง
      if (el) { el.value = ''; el.click(); }
    },
    onOfflineFileChosen(evt) {
      const file = evt && evt.target && evt.target.files && evt.target.files[0];
      if (file) this.readOfflineFile(file);
    },
    onOfflineDrop(evt) {
      this.offlineDragOver = false;
      const file = evt && evt.dataTransfer && evt.dataTransfer.files && evt.dataTransfer.files[0];
      if (file) this.readOfflineFile(file);
    },
    /**
     * Read → validate (`parseTaxLogExport`: kind, schema major, shape) → hand to the store.
     * Setting the store flips `dataSourceKey`, and its watcher runs `loadAll` against the
     * file adapter — this method loads nothing itself.
     */
    readOfflineFile(file) {
      this.offlineError = null;
      this.offlineReading = true;
      const name = file.name || '';
      const reader = new FileReader();
      reader.onload = () => {
        this.offlineReading = false;
        const res = parseTaxLogExport(String(reader.result || ''));
        if (!res.ok) {
          this.offlineError = res.error;
          return;
        }
        this.offlineWarningsOpen = false;
        setOfflineTaxLog(res.file, name);
      };
      reader.onerror = () => {
        this.offlineReading = false;
        this.offlineError = 'อ่านไฟล์ไม่สำเร็จ';
      };
      reader.readAsText(file);
    },
    /** Banner → back to the picker. Clearing the store resets the page via the `dataSourceKey` watcher. */
    openAnotherOfflineFile() {
      clearOfflineTaxLog();
      this.offlineError = null;
      this.offlineWarningsOpen = false;
      this.inspectorFull = false;
      // `?form=` / `?turn=` named a form / turn of the file just closed — drop them, or the
      // next file would be opened at whatever happens to share those numbers.
      const query = this.$route.query || {};
      if (Object.keys(query).length) {
        this.$router.replace({ query: {} }).catch(() => {
          /* redundant navigation — harmless */
        });
      }
    },
    /** `warnings[]` are strings from the BE; anything else is shown as JSON rather than "[object Object]". */
    warningText(w) {
      if (w === null || w === undefined) return '—';
      return typeof w === 'string' ? w : JSON.stringify(w);
    },
    /**
     * Playground snapshots can carry an `editRef` per prompt layer. Opening the
     * config page in a NEW TAB is a plain navigation — this page still writes
     * nothing, and the reader keeps their place in the log.
     */
    openEdit(ref) {
      if (!ref) return;
      let href = '';
      if (ref.kind === 'configVersion') {
        href = '/TaxConfigurationV2';
      } else {
        const base = `/TaxQuestionV2/${ref.configVersionId}/${ref.questionVersionId}`;
        const qs = [];
        if (ref.groupCode) qs.push(`section=${encodeURIComponent(ref.groupCode)}`);
        if (ref.kind === 'questionItem' && ref.questionItemId) {
          qs.push(`item=${encodeURIComponent(ref.questionItemId)}`);
        }
        href = qs.length ? `${base}?${qs.join('&')}` : base;
      }
      if (href) window.open(href, '_blank');
    },
    /** A4 — every copyable block funnels through here, including the fallback path. */
    async onCopy(payload) {
      const text = String((payload && payload.text) ?? '');
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          this.showSnack('คัดลอกแล้ว', 1500);
          return;
        } catch (err) {
          // not https / permission denied → fall through to the manual path
        }
      }
      if (this.legacyCopy(text)) {
        this.showSnack('คัดลอกแล้ว', 1500);
        return;
      }
      this.showSnack('กด Ctrl/⌘ + C เพื่อคัดลอก', 3500, 'warning');
    },
    /** Leaves the text SELECTED so `Ctrl/⌘ + C` still works when execCommand is blocked. */
    legacyCopy(text) {
      try {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', 'readonly');
        area.style.position = 'fixed';
        area.style.top = '0';
        area.style.left = '0';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand && document.execCommand('copy');
        document.body.removeChild(area);
        return Boolean(ok);
      } catch (err) {
        return false;
      }
    },

    // ── misc ──────────────────────────────────────────────────────────
    goBack() {
      this.$router.push({ name: 'TaxLogV2' });
    },
    statusTh(status) {
      return FORM_STATUS_TH[status] || status || '—';
    },
    statusColor(status) {
      return FORM_STATUS_COLOR[status] || 'grey';
    },
    postCompleteEditTh(value) {
      return POST_COMPLETE_EDIT_TH[value] || value || '';
    },
    postCompleteEditColor(value) {
      return POST_COMPLETE_EDIT_COLOR[value] || 'grey';
    },
    stepStatusColor(status) {
      const s = String(status || '').toLowerCase();
      if (s === 'success' || s === 'completed' || s === 'ok') return 'success';
      if (s === 'error' || s === 'failed' || s === 'fail') return 'error';
      if (s === 'skipped') return 'grey';
      return 'primary';
    },
    stepMs(st) {
      const ms = st && st.responseTimeMs;
      return ms === null || ms === undefined ? '—' : `${Math.round(ms)} ms`;
    },
    stepPreview(st) {
      const text = (st && st.output && st.output.message) || (st && st.input && st.input.message) || '';
      return String(text).replace(/\s+/g, ' ').trim();
    },
    // 🔴 ทั้งคู่เป็น **เวลาไทย** ไม่ใช่เวลาเครื่องและไม่ใช่ UTC — ค่าบน wire เป็น UTC ที่ไม่มี `Z`
    //    ต่อท้าย ⇒ `new Date(v)` เดิมไม่แปลงอะไรเลย จอเลยช้ากว่าจริง 7 ชั่วโมง (ดู `utils/thaiTime.js`)
    //    รูปแบบถูกตรึงเป็น 24 ชม. ด้วย แทน `toLocaleTimeString()`/`toLocaleString()` ที่เปลี่ยนหน้าตา
    //    ไปตาม locale ของเบราว์เซอร์ผู้ใช้
    fmtTime(value) {
      return fmtThaiTime(value);
    },
    fmtDateTime(value) {
      return fmtThaiDateTime(value);
    },
    formatTokenCount(value) {
      if (value === null || value === undefined) return '—';
      const number = Number(value);
      return Number.isFinite(number) ? number.toLocaleString() : '—';
    },
    formatTokenCost(value) {
      if (value === null || value === undefined) return '—';
      const number = Number(value);
      if (!Number.isFinite(number)) return '—';
      if (number > 0 && number < 0.0001) return '<$0.0001';
      return `$${number.toFixed(4)}`;
    },
    tokenRateTooltip(rate, cost) {
      const number = Number(rate);
      if (rate !== null && rate !== undefined && Number.isFinite(number)) {
        const formatted = number.toFixed(8).replace(/0+$/, '').replace(/\.$/, '');
        return `$${formatted} per 1,000 tokens`;
      }
      if (cost !== null && cost !== undefined) return 'Estimated from multiple model rates';
      return 'Price unavailable';
    },
    /**
     * 🔴 `color` is a REQUIRED part of the message, not decoration: this snackbar
     * also carries "ไม่พบเทิร์น #N" and "กด Ctrl/⌘ + C เพื่อคัดลอก", and a green
     * bar on a failure reads as "done". It defaults to `success` so the copy
     * confirmations stay exactly as they were.
     */
    showSnack(message, timeout, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color || 'success';
      this.snackbarTimeout = timeout || 3500;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
/* 🔴 Copied from `TaxPlaygroundV2.vue` (its `<style scoped>`) so the two pages
   read as siblings — same column heights, same rail, same tabs, same dark
   `.code-block`, same `:deep()` density rules for the two shared child panels.
   The playground-only bits are dropped (`.tpg-state-message` no longer exists over there either — the
   peek is a chat bubble now); `.txl-step` is
   the one addition (the production flow-step list has no twin over there). */
/* ── the shell ────────────────────────────────────────────────────────────
   ONE scroll region per column, never a page-level one on top of them.

   🔴 `height: 100%`, and deliberately NOT `calc(100vh - <chrome>)`. `.v-main` is a
   `flex: 1` item of `.v-application__wrap`, so its height is definite and 100%
   resolves against its CONTENT box — minus the app-bar padding — with no arithmetic
   and nothing to keep in sync. The `calc(100vh - 320px)`/`- 280px` this replaced
   guessed the chrome ~140 px short, so the columns hung below the fold and the page
   grew a SECOND scrollbar under the column ones.

   `overflow-y: auto` rather than `hidden`: on a viewport too short to hold the
   chrome plus the columns' `min-height` the shell degrades to a single page
   scrollbar instead of clipping the toolbar out of reach. */
.tpg-page {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100%;
  overflow-y: auto;
}

.tpg-container {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 12px 16px;
}

/* Chrome (toolbar, banners, header card, turn strip) keeps its natural height;
   only the row below is elastic. */
.tpg-container > *:not(.tpg-row) {
  flex: 0 0 auto;
}

.tpg-row {
  flex: 1 1 auto;
  min-height: 320px;
}

.tpg-col {
  height: 100%;
  min-height: 0;
  overflow-y: auto;
}

/* ── chrome density ───────────────────────────────────────────────────────
   Both are global admin-house classes (`assets/styles/globals.css`) whose stock
   gaps — 25 px above the toolbar, 2 rem below the card — are sized for a page
   that scrolls. In a shell every one of those pixels is taken off the columns,
   so they are tightened HERE only, leaving the other admin views alone. */
.header-title {
  margin-top: 4px;
}

.card2 {
  margin-bottom: 12px;
  padding-top: 8px;
}

/* Collapsed timeline — a rail wide enough for one icon button and a vertical
   label. Two classes on purpose: `.v-col-auto` also sets width/max-width, and
   scoped styles are not guaranteed to be injected after Vuetify's sheet. */
.tpg-col.tpg-col--rail {
  flex: 0 0 44px;
  width: 44px;
  min-width: 44px;
  max-width: 44px;
  overflow-x: hidden;
}

.tpg-rail-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding-top: 2px;
}

.tpg-rail-label {
  writing-mode: vertical-rl;
  font-size: 11px;
  white-space: nowrap;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.tpg-inspector {
  display: flex;
  flex-direction: column;
  overflow-y: hidden;
}

.tpg-inspector-head {
  position: sticky;
  top: 0;
  z-index: 2;
  min-height: 40px;
  background: rgb(var(--v-theme-surface));
}

/* The ONE pane of the inspector column — it takes every pixel the head left over
   and scrolls on its own. */
.tpg-pane {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

/* 🔴 `flex: none` — Vuetify's horizontal `.v-tabs` does NOT set it, so the moment
   this strip becomes a flex item next to tall content it gets squeezed and the
   Thai labels are clipped mid-character. */
.tpg-tabs {
  flex: none;
  position: sticky;
  top: 0;
  z-index: 1;
  background: rgb(var(--v-theme-surface));
}

.tpg-turn-card {
  cursor: pointer;
}

.tpg-turn--active {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(30, 136, 229, 0.08);
}

.tpg-strip {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  white-space: nowrap;
  padding-bottom: 4px;
}

.tpg-strip-card {
  flex: 0 0 150px;
  width: 150px;
  padding: 6px 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  cursor: pointer;
}

.tpg-form-pane,
.tpg-json-pane {
  min-height: 0;
}

/* Flow steps of a production turn (`ChatWorkFlow`) — a compact reading list, not
   a table: the only columns that matter are node · status · ms. The hairline is a
   list rule (it groups the wrapped lines of one step), deliberately NOT an accent
   bar: the status chip already carries this row's colour. */
.txl-step {
  border-left: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 2px 0 2px 8px;
  margin-bottom: 6px;
  max-width: 100%;
  overflow: hidden;
}

/* Offline picker drop zone — same look as `TaxBundleCompareV2Dialog.vue`'s `.tbc-drop`. */
.txl-drop {
  border: 1px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.txl-drop--over {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.06);
}

.txl-drop--error {
  border-color: rgb(var(--v-theme-error));
}

/* The banner sits in the shell's fixed chrome — a long warning list must scroll in
   its own box, never push the columns below their `min-height`. */
.txl-offline-warnings {
  max-height: 160px;
  overflow-y: auto;
  margin: 0;
  padding-left: 18px;
  overflow-wrap: anywhere;
}

/* 🔴 `overflow-wrap: anywhere` — NOT `word-break: break-word/break-all`. A break
   is allowed only when the token genuinely does not fit, so field keys stop being
   chopped in half mid-word, while Thai (no break opportunities at all) can still
   wrap. Anything still too long scrolls INSIDE this box. */
.code-block {
  font-family: monospace;
  font-size: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: normal;
  overflow-wrap: anywhere;
  background: #0f1115;
  color: #dbe2ea;
  border-radius: 6px;
  padding: 8px;
  max-height: 34vh;
  overflow: auto;
}

/* ── inspector density (the column the admin actually reads) ─────────────
   These reach into the two shared child panels with `:deep()` on purpose: they
   are pure presentation of THIS column's cramped width. */

/* The six status chips wrap onto a tidy second line instead of overflowing. */
.tpg-inspector :deep([data-testid='tpg-cf-counts']) {
  row-gap: 4px;
  column-gap: 4px;
  max-width: 100%;
}

.tpg-inspector :deep([data-testid='tpg-cf-counts'] .v-chip) {
  flex: 0 1 auto;
  max-width: 100%;
}

/* Form Template table: the value column gets room, the status chip gets a gutter,
   and a table too wide for the column scrolls in its OWN box (never the page). */
.tpg-inspector :deep(.cf-panels .v-expansion-panel-text__wrapper) {
  overflow-x: auto;
}

.tpg-inspector :deep(.cf-table) {
  min-width: 320px;
}

.tpg-inspector :deep(.cf-table td) {
  padding: 4px 8px;
}

.tpg-inspector :deep(.cf-label) {
  width: 36%;
}

.tpg-inspector :deep(.cf-status) {
  width: 24%;
  padding-right: 12px;
}

.tpg-inspector :deep(.cf-value) {
  width: 40%;
  word-break: normal;
  overflow-wrap: anywhere;
}

.tpg-inspector :deep(.cf-cust) {
  word-break: normal;
  overflow-wrap: anywhere;
}

/* Same mid-word rule for the dark boxes owned by the child panels. */
.tpg-inspector :deep(.code-block) {
  word-break: normal;
  overflow-wrap: anywhere;
  overflow-x: auto;
}
</style>
